/**
 * ConfigPage — CFG-01 (Perfil de Empresa) + CFG-02 (Localización)
 * Organizado en tabs dentro de una misma página de Configuración.
 */
import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import DatePicker from '@/components/DatePicker'
import { Button } from '@/components/Button'
import { PdfTemplatesTab } from './PdfTemplatesTab'
import { DTEConfigTab } from './DTEConfigTab'
import AuditLogTab from './AuditLogTab'
import { TenantNotificationsTab } from './TenantNotificationsTab'
import { HrDocumentSettingsTab } from './HrDocumentSettingsTab'
import { SeriesTab } from './SeriesTab'
import { SolicitarFoliosModal } from './SolicitarFoliosModal'
import { etiquetasNumeracion, paisUsaDetraccion, usaSeriesDeEmision } from '@/lib/documentNumbering'
import { HistoricalImportPage } from '@/modules/historical_import/components/HistoricalImportPage'
import { Building2, ListPlus, Save, Upload, CheckCircle2, AlertCircle, Loader2, Pencil, Trash2, ChevronUp, ChevronDown, ShieldCheck, Zap, Eye, EyeOff, Copy, Check, RefreshCw, DollarSign, Plus, X, BarChart3, Bell, ChevronRight, Activity, HelpCircle, Download } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { HelpPanel } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { formatRut } from '@/lib/utils'
import { extractFieldErrors, extractErrorMessage } from '@/lib/errorUtils'
import { inputCls } from '@/lib/formStyles'
import { useTenantLocale, formatDate, formatDateTime } from '@/lib/locale'
import {
  useCompanyProfile,
  useUpdateCompanyProfile,
  useLocalizationConfig,
  useUpdateLocalizationConfig,
  useFolioConfigs,
  useUpdateFolioConfig,
  useGlobalCustomFields,
  useCreateGlobalCustomField,
  useUpdateGlobalCustomField,
  useDeleteGlobalCustomField,
  useInvoicerConfig,
  useUpdateInvoicerConfig,
  useTestInvoicer,
  useInvoicerProviders,
  type FolioConfig,
  type GlobalCustomField,
  type InvoicerConfig,
  type InvoicerProvider,
} from '@/modules/config/hooks/useConfig'
import { usePaymentGateways, useUpdateGateway, useTestGateway } from '@/modules/payments/hooks/usePayments'
import type { PaymentGateway } from '@/modules/payments/hooks/usePayments'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import {
  useSSOConfig as useTenantSSOConfig,
  useUpdateSSOConfig,
  useRegenerateSAMLSPCert,
  useSAMLMetadataUrl,
  type TenantSSOConfig,
  type UpdateSSOConfigPayload,
  useAPIKeys,
  useCreateAPIKey,
  useUpdateAPIKey,
  useDeleteAPIKey,
  useAPIKeyScopes,
  useAPIKeyUsage,
  useAPIKeyAlerts,
  useAPIKeyGlobalUsage,
  type APIKeyItem,
  type APIKeyAlert,
  type ScopeGroup,
  useCurrencyRates,
  useSyncCurrencyRates,
  useCertificateInfo,
  useUploadCertificate,
  useCAFList,
  useEstadoFolios,
  useUploadCAF,
  type CertificateInfo,
  type CAFInfo,
} from '@/modules/config/hooks/useConfig'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/ds/DataTable'
import { useEtiquetaAsociada } from '@/lib/etiquetaAsociada'
import { Modal } from '@/components/ds/Modal'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function CopyableField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div>
      <p className="text-xs text-content-muted mb-1">{label}</p>
      <div className="flex items-center gap-2 bg-surface border border-divider rounded-lg px-3 py-2">
        <code className="text-xs text-content flex-1 break-all">{value}</code>
        <button type="button" onClick={copy} className="text-content-subtle hover:text-content-muted shrink-0">
          {copied ? <Check size={14} className="text-success-solid" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  )
}

function CertExpiryBadge({ notAfter }: { notAfter: string | null }) {
  if (!notAfter) return null
  const daysLeft = Math.ceil((new Date(notAfter).getTime() - Date.now()) / 86400000)
  const color = daysLeft <= 7 ? 'red' : daysLeft <= 30 ? 'amber' : 'green'
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
      ${color === 'red' ? 'bg-danger-surface-strong text-danger-content' : color === 'amber' ? 'bg-warning-surface-strong text-warning-content' : 'bg-success-surface-strong text-success-content'}`}>
      {daysLeft > 0 ? `Expira en ${daysLeft}d` : 'Expirado'}
    </span>
  )
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-solid flex items-center gap-1"><AlertCircle size={12} />{message}</p>
}

function FormField({
  label, required, error, children, hint, htmlFor,
}: {
  label: string; required?: boolean; error?: string; children: React.ReactNode
  hint?: string; htmlFor?: string
}) {
  // El id lo genera el envoltorio y viaja al control. ⚠️ No se resuelve
  // envolviendo con el `<label>`: este campo también recibe el selector de
  // color, que trae botones de preset adentro — dentro de una etiqueta, el
  // clic en un preset abriría además el selector nativo. Ver `etiquetaAsociada`.
  const { htmlFor: id, campo } = useEtiquetaAsociada(children, htmlFor)
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-content mb-1">
        {label}{required && <span className="text-danger-solid ml-0.5">*</span>}
      </label>
      {campo}
      {hint && !error && <p className="mt-1 text-xs text-content-subtle">{hint}</p>}
      <FieldError message={error} />
    </div>
  )
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div>
      <label className="block text-xs font-medium text-content-muted mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input aria-label={label} readOnly value={value} className="flex-1 rounded-md border border-divider bg-surface-muted px-3 py-1.5 text-sm text-content-muted font-mono" />
        <button type="button" onClick={handleCopy} title="Copiar" aria-label="Copiar"
          className="p-1.5 rounded-md border border-divider hover:bg-soft text-content-muted transition-colors">
          {copied ? <Check size={14} className="text-success-solid" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  )
}

// ─── Color picker para sidebar ─────────────────────────────────────────────────

function SidebarColorPicker({ defaultValue }: { defaultValue: string }) {
  const [color, setColor] = useState(defaultValue || '#0C1824')
  const PRESETS = ['#0C1824', '#182065', '#1a3c34', '#4c0519', '#1e1b4b', '#374151', '#7c2d12', '#134e4a']
  return (
    <FormField label="Color del Sidebar"
      hint="Color del menú lateral para identificar esta empresa. Útil al trabajar con varias empresas.">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <input
            type="color"
            name="sidebar_color"
            value={color}
            onChange={e => setColor(e.target.value)}
            className="h-10 w-16 rounded-lg border border-divider cursor-pointer p-1 bg-surface"
          />
          <input
            readOnly
            value={color}
            className={`${inputCls} flex-1 font-mono text-xs`}
          />
          {/* Vista previa */}
          <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/80"
            style={{ backgroundColor: color }}>
            <span className="w-3 h-3 rounded-full bg-white/20" />
            Preview
          </div>
        </div>
        {/* Colores predefinidos */}
        <div className="flex gap-2 flex-wrap">
          {PRESETS.map(preset => (
            <button
              key={preset}
              type="button"
              title={preset}
              onClick={() => setColor(preset)}
              className={`w-7 h-7 rounded-lg border-2 transition-all ${color === preset ? 'border-primary scale-110' : 'border-transparent hover:border-divider-strong'}`}
              style={{ backgroundColor: preset }}
            />
          ))}
        </div>
      </div>
    </FormField>
  )
}

// ─── Color picker genérico para primary/accent (brand) ───────────────────────

function BrandColorPicker({
  name, label, hint, defaultValue, presets, previewAs = 'button',
}: {
  name: 'primary_color' | 'accent_color'
  label: string
  hint: string
  defaultValue: string
  presets: string[]
  previewAs?: 'button' | 'badge'
}) {
  const [color, setColor] = useState(defaultValue || presets[0])

  const preview = previewAs === 'button' ? (
    <div
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      Botón
    </div>
  ) : (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: color }}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
      Badge
    </span>
  )

  return (
    <FormField label={label} hint={hint}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <input
            type="color"
            name={name}
            value={color}
            onChange={e => setColor(e.target.value)}
            className="h-10 w-16 rounded-lg border border-divider cursor-pointer p-1 bg-surface"
          />
          <input
            readOnly
            value={color}
            className={`${inputCls} flex-1 font-mono text-xs`}
          />
          <div className="flex items-center">{preview}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {presets.map(preset => (
            <button
              key={preset}
              type="button"
              title={preset}
              onClick={() => setColor(preset)}
              className={`w-7 h-7 rounded-lg border-2 transition-all ${color === preset ? 'border-primary scale-110' : 'border-transparent hover:border-divider-strong'}`}
              style={{ backgroundColor: preset }}
            />
          ))}
        </div>
      </div>
    </FormField>
  )
}

// ─── Toast simple ─────────────────────────────────────────────────────────────

function Toast({ message, type }: { message: string; type: 'success' | 'error' }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-fade-in
      ${type === 'success' ? 'bg-success-fill text-white' : 'bg-danger-fill text-white'}`}>
      {type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
      {message}
    </div>
  )
}

// ─── CFG-01: Perfil de Empresa ────────────────────────────────────────────────

// All available modules (key → label)
const ALL_MODULES = [
  { key: 'inventory',    label: 'Inventario',            description: 'Control de stock, bodegas y movimientos.' },
  { key: 'pos',          label: 'Punto de venta',        description: 'Locales y terminales del POS: activar cajas con un código y revocarlas.' },
  { key: 'payroll',      label: 'Nómina',                description: 'Empleados, contratos, liquidaciones y vacaciones.' },
  { key: 'treasury',     label: 'Tesorería',             description: 'Factoring: entidades, operaciones y reportes.' },
  { key: 'receivables',  label: 'Cuentas por Cobrar',    description: 'Cobranza, antigüedad, estados de cuenta, recordatorios y créditos de clientes.' },
  { key: 'payables',     label: 'Cuentas por Pagar',     description: 'Pagos a proveedores, antigüedad, estados de cuenta, calendario y flujo proyectado.' },
  { key: 'commissions',  label: 'Comisiones',            description: 'Comisiones a vendedores por línea de pedido.' },
  { key: 'subscriptions', label: 'Suscripciones',       description: 'Facturación recurrente automática.' },
  { key: 'forecast',     label: 'Forecast',              description: 'Proyección de ventas a 6 meses.' },
  { key: 'reports',      label: 'Centro de Reportes',   description: 'Reportes exportables: ventas, compras, aging, F29.' },
  { key: 'intl_invoices', label: 'Invoices Internacionales', description: 'Contabilización automática de invoices USD de partners (licencias SaaS). Habilita una casilla de email y parseo con IA.' },
  { key: 'honorarios',   label: 'Honorarios (BHE Chile)', description: 'Recepción automática de Boletas de Honorarios desde el SII. Parser XML + voucher contable con 3 líneas (gasto / por pagar / retención 2da cat.).' },
]

function CompanyProfileTab() {
  const { data: profile, isLoading } = useCompanyProfile()
  const { data: locConfig } = useLocalizationConfig()
  const { mutate: update, isPending } = useUpdateCompanyProfile()
  // La cuenta de detracciones solo existe en el régimen peruano (SPOT). El resto
  // de los países no debe ver un campo que su normativa no contempla.
  const usaDetraccion = paisUsaDetraccion(locConfig?.country_code)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [disabledModules, setDisabledModules] = useState<string[]>([])

  // Sync disabledModules from profile when it loads
  useEffect(() => {
    if (profile?.disabled_modules) setDisabledModules(profile.disabled_modules)
  }, [profile?.disabled_modules])

  const toggleModule = (key: string, enabled: boolean) => {
    setDisabledModules(prev =>
      enabled ? prev.filter(m => m !== key) : [...prev.filter(m => m !== key), key]
    )
  }

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3500)
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setErrors(p => ({ ...p, logo: 'Solo se permiten imágenes JPG, PNG o WEBP.' }))
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(p => ({ ...p, logo: 'El archivo no puede superar los 5 MB.' }))
      return
    }
    setErrors(p => ({ ...p, logo: '' }))
    const reader = new FileReader()
    reader.onloadend = () => setLogoPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)

    // Validación básica
    const newErrors: Record<string, string> = {}
    if (!fd.get('legal_name')) newErrors.legal_name = 'La Razón Social es obligatoria.'
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }
    setErrors({})

    // Si no se subió logo nuevo, quitar el campo
    const logoFile = fileRef.current?.files?.[0]
    if (!logoFile) fd.delete('logo')

    // Módulos desactivados — serializar como JSON string para DRF JSONField
    fd.append('disabled_modules', JSON.stringify(disabledModules))

    // Footer Clobi — toggle (validación server-side bloquea desactivar en trial/demo)
    const clobiFooter = fd.get('show_clobi_footer')
    if (clobiFooter === null) fd.append('show_clobi_footer', 'false')

    // Un checkbox desmarcado NO viaja en el FormData: sin esto, apagar el organigrama
    // del portal no se guardaría nunca (el campo llegaría ausente y el serializer
    // dejaría el valor anterior).
    if (fd.get('org_chart_visible_to_employees') === null) {
      fd.append('org_chart_visible_to_employees', 'false')
    }

    update(fd, {
      onSuccess: () => showToast('Perfil actualizado correctamente.', 'success'),
      onError: (err: unknown) => {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors) setErrors(p => ({ ...p, ...fieldErrors }))
        const firstFieldMsg = fieldErrors ? Object.values(fieldErrors)[0] : null
        showToast(
          firstFieldMsg || extractErrorMessage(err, 'Error al guardar. Intenta nuevamente.'),
          'error',
        )
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-content-subtle" size={28} />
      </div>
    )
  }

  const logoSrc = logoPreview || profile?.logo_url

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl">
      {/* Logo */}
      <div className="flex items-start gap-6 p-5 bg-glass-3 rounded-xl border border-divider">
        <div className="w-20 h-20 rounded-xl bg-surface border-2 border-dashed border-divider-strong flex items-center justify-center overflow-hidden shrink-0">
          {logoSrc
            ? <img src={logoSrc} alt="Logo" className="w-full h-full object-contain" />
            : <Building2 size={28} className="text-content-subtle" />
          }
        </div>
        <div>
          <p className="text-sm font-medium text-content mb-1">Logo de la empresa</p>
          <p className="text-xs text-content-subtle mb-3">JPG, PNG o WEBP · Máximo 5 MB</p>
          <button type="button" onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-divider-strong rounded-lg bg-surface hover:bg-soft transition-colors text-content">
            <Upload size={13} /> Cambiar logo
          </button>
          <input ref={fileRef} type="file" name="logo" accept="image/*"
            onChange={handleLogoChange} className="hidden" />
          <FieldError message={errors.logo} />
        </div>
      </div>

      {/* Datos básicos */}
      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Datos de la empresa</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Razón Social" required error={errors.legal_name}>
            <input name="legal_name" defaultValue={profile?.legal_name} placeholder="Nombre legal" className={inputCls} />
          </FormField>
          <FormField label="Nombre de Fantasía">
            <input name="trade_name" defaultValue={profile?.trade_name} placeholder="Nombre comercial (opcional)" className={inputCls} />
          </FormField>
          <FormField label="RUT / Identificador Tributario" error={errors.tax_id}
            hint="Formato: 12.345.678-9">
            <input name="tax_id" defaultValue={profile?.tax_id ? formatRut(profile.tax_id) : ''} placeholder="76.354.771-9" className={inputCls} />
          </FormField>
          <FormField label="Giro / Actividad Económica">
            <input name="business_activity" defaultValue={profile?.business_activity} placeholder="Ej: Servicios de software" className={inputCls} />
          </FormField>
        </div>
      </section>

      {/* Contacto */}
      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Contacto</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Email de contacto">
            <input name="email" type="email" defaultValue={profile?.email} placeholder="contacto@empresa.cl" className={inputCls} />
          </FormField>
          <FormField label="Teléfono">
            <input name="phone" defaultValue={profile?.phone} placeholder="+56 9 1234 5678" className={inputCls} />
          </FormField>
          <FormField label="Sitio web">
            <input name="website" defaultValue={profile?.website} placeholder="https://empresa.cl" className={inputCls} />
          </FormField>
        </div>
      </section>

      {/* Dirección */}
      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Dirección</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <FormField label="Calle y número">
              <input name="address_street" defaultValue={profile?.address_street} placeholder="Av. Providencia 1234, Of. 501" className={inputCls} />
            </FormField>
          </div>
          {/* Va en Dirección porque es un lugar fisico, pero NO es la direccion
              tributaria: es donde llega la mercaderia, y se declara al SII en el
              acuse de recibo. Antes no existia y el acuse se armaba con la calle
              de arriba o, si estaba vacia, con el literal «Oficina». */}
          <div className="sm:col-span-2">
            <FormField
              label="Recinto de recepción"
              hint="Dónde se reciben las mercaderías. Se declara al SII al dar el acuse de recibo; si hay más de una sucursal, se puede cambiar en cada acuse."
            >
              <input
                name="reception_venue"
                defaultValue={profile?.reception_venue}
                maxLength={80}
                placeholder="Bodega Central, Av. Providencia 1234"
                className={inputCls}
              />
            </FormField>
          </div>
          {/* Hasta #1618 no existía y el XML llevaba la ciudad como comuna. Sin
              comuna sigue saliendo la ciudad, así que el aviso no bloquea. */}
          <FormField
            label="Comuna"
            hint={locConfig?.country_code === 'CL'
              ? (profile && !profile.address_commune
                ? 'Falta la comuna: el SII la pide en cada documento. Mientras falte, se informa la ciudad.'
                : 'Se informa al SII en cada documento emitido.')
              : undefined}
          >
            <input name="address_commune" defaultValue={profile?.address_commune} maxLength={100} placeholder="Providencia" className={inputCls} />
          </FormField>
          <FormField label="Ciudad">
            <input name="address_city" defaultValue={profile?.address_city} placeholder="Santiago" className={inputCls} />
          </FormField>
          <FormField label="Región">
            <input name="address_region" defaultValue={profile?.address_region} placeholder="Metropolitana" className={inputCls} />
          </FormField>
          <FormField label="Código postal">
            <input name="address_postal_code" defaultValue={profile?.address_postal_code} placeholder="7500000" className={inputCls} />
          </FormField>
          <div className="sm:col-span-2">
            <FormField
              label="Direcciones adicionales (PDF)"
              hint="Aparecen en el encabezado del PDF debajo de los datos de la empresa. Para listar sucursales u oficinas comerciales. NO afecta al XML enviado al SII — el DirOrigen legal sigue siendo la dirección principal de arriba."
            >
              <textarea
                name="additional_addresses"
                defaultValue={profile?.additional_addresses || ''}
                rows={4}
                placeholder={`OFICINAS GENERALES:\nAvenida Libertad 821 - Teléfono 56-(42)-2237960 Chillán\nAvenida del Parque 4980, oficina 231. Ciudad Empresarial, Huechuraba, Santiago.`}
                className={`${inputCls} font-mono text-xs leading-relaxed`}
              />
            </FormField>
          </div>
          {usaDetraccion && (
            <div className="sm:col-span-2">
              <FormField
                label="Cuenta de detracciones del Banco de la Nación"
                hint="Cuenta corriente de detracciones (SPOT) donde el cliente deposita el porcentaje detraído. Va dentro del XML de cada factura afecta. Sin este dato una factura con detracción no se puede emitir: el comprobante le exige al cliente detraer un porcentaje y tiene que decirle a qué cuenta depositarlo."
              >
                <input
                  name="detraction_account"
                  defaultValue={profile?.detraction_account || ''}
                  placeholder="00-000-000000"
                  className={`${inputCls} font-mono`}
                />
              </FormField>
            </div>
          )}
        </div>
      </section>

      {/* Personalización */}
      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-1">Personalización Visual</h3>
        <p className="text-xs text-content-subtle mb-4">
          Colores de marca — se aplican a botones, enlaces y elementos destacados en toda la plataforma.
          Las variantes claras/oscuras se calculan automáticamente para cumplir contraste AA.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SidebarColorPicker defaultValue={profile?.sidebar_color || '#0C1824'} />
          <BrandColorPicker
            name="primary_color"
            label="Color Primario"
            hint="Color principal de marca (botones, links, acentos). Se adapta a dark mode automáticamente."
            defaultValue={profile?.primary_color || '#327453'}
            presets={['#327453', '#2563EB', '#0EA5E9', '#7C3AED', '#DB2777', '#DC2626', '#EA580C', '#059669']}
            previewAs="button"
          />
          <BrandColorPicker
            name="accent_color"
            label="Color de Acento"
            hint="Color secundario (acciones positivas, éxito). Vacío = default plataforma."
            defaultValue={profile?.accent_color || '#48A878'}
            presets={['#48A878', '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#8B5CF6', '#F59E0B', '#EC4899']}
            previewAs="badge"
          />
        </div>

        <div className="mt-6 pt-4 border-t border-divider">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="show_clobi_footer"
              value="true"
              defaultChecked={profile?.show_clobi_footer ?? true}
              className="mt-0.5 rounded border-divider-strong text-primary focus:ring-primary"
            />
            <div>
              <p className="text-sm text-content font-medium">Mostrar "Generado con Clobi" al pie del PDF</p>
              <p className="text-xs text-content-subtle">
                Línea discreta con un enlace a clobi.io al final de cada documento emitido. En cuentas trial/demo se mantiene siempre activo.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="org_chart_visible_to_employees"
              value="true"
              defaultChecked={profile?.org_chart_visible_to_employees ?? true}
              className="mt-0.5 rounded border-divider-strong text-primary focus:ring-primary"
            />
            <div>
              <p className="text-sm text-content font-medium">
                Publicar el organigrama en el portal del colaborador
              </p>
              <p className="text-xs text-content-subtle">
                Los trabajadores pueden ver quién es quién desde su portal: nombre, cargo y
                jefatura, sin RUT ni datos de contacto. Desactívalo si prefieres no publicar
                la estructura (por ejemplo durante una reorganización). No afecta al
                organigrama del sistema, que sigue disponible para quien tenga el permiso.
              </p>
            </div>
          </label>
        </div>
      </section>

      {/* Módulos activos */}
      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-1">Módulos activos</h3>
        <p className="text-xs text-content-subtle mb-4">
          Los módulos desactivados se ocultan del menú lateral y de los formularios donde aparecen. No se borran datos.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ALL_MODULES.map(({ key, label, description }) => {
            const enabled = !disabledModules.includes(key)
            return (
              <label key={key}
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors
                  ${enabled ? 'bg-primary-50 border-primary/30' : 'bg-surface-muted border-divider opacity-60'}`}>
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary"
                  checked={enabled}
                  onChange={e => toggleModule(key, e.target.checked)}
                />
                <div>
                  <p className="text-sm font-medium text-content">{label}</p>
                  <p className="text-xs text-content-subtle">{description}</p>
                </div>
              </label>
            )
          })}
        </div>
      </section>

      <div className="flex justify-end pt-2">
        <button type="submit" disabled={isPending}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
          {isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {isPending ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>

      {toast && <Toast {...toast} />}
    </form>
  )
}

// ─── CFG-02: Localización ─────────────────────────────────────────────────────

function LocalizationTab() {
  const { data: loc, isLoading } = useLocalizationConfig()
  const { mutate: update, isPending } = useUpdateLocalizationConfig()
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [resDate, setResDate] = useState(loc?.resolucion_date || '')
  useEffect(() => { if (loc?.resolucion_date) setResDate(loc.resolucion_date) }, [loc?.resolucion_date])

  // ── Impuestos Adicionales ────────────────────────────────────────────────────
  type TaxDraft = { key: string; label: string; help_text: string; rate: number }
  const EMPTY_TAX: TaxDraft = { key: '', label: '', help_text: '', rate: 0 }
  const [additionalTaxes, setAdditionalTaxes] = useState<TaxDraft[]>([])
  const [taxModal, setTaxModal] = useState<{ open: boolean; idx: number | null; draft: TaxDraft }>({
    open: false, idx: null, draft: EMPTY_TAX,
  })
  const [taxErrors, setTaxErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (loc?.additional_taxes) {
      setAdditionalTaxes(loc.additional_taxes.map(t => ({
        key: t.key || '',
        label: t.label || '',
        help_text: t.help_text || '',
        rate: t.rate ?? 0,
      })))
    }
  }, [loc?.additional_taxes])

  const openAddTax = () => setTaxModal({ open: true, idx: null, draft: EMPTY_TAX })
  const openEditTax = (idx: number) => setTaxModal({ open: true, idx, draft: { ...additionalTaxes[idx] } })
  const closeTaxModal = () => { setTaxModal({ open: false, idx: null, draft: EMPTY_TAX }); setTaxErrors({}) }

  const autoKey = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '')

  const saveTax = () => {
    const d = taxModal.draft
    const errs: Record<string, string> = {}
    if (!d.label.trim()) errs.label = 'El nombre es obligatorio.'
    if (isNaN(d.rate) || d.rate < 0 || d.rate > 100) errs.rate = 'La tasa debe ser un número entre 0 y 100.'
    if (Object.keys(errs).length) { setTaxErrors(errs); return }
    const entry: TaxDraft = { ...d, key: d.key || autoKey(d.label) }
    if (taxModal.idx === null) {
      setAdditionalTaxes(prev => [...prev, entry])
    } else {
      setAdditionalTaxes(prev => prev.map((t, i) => i === taxModal.idx ? entry : t))
    }
    closeTaxModal()
  }

  const deleteTax = (idx: number) =>
    setAdditionalTaxes(prev => prev.filter((_, i) => i !== idx))

  const moveTax = (idx: number, dir: -1 | 1) => {
    setAdditionalTaxes(prev => {
      const arr = [...prev]
      const swap = idx + dir
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]]
      return arr
    })
  }

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3500)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const newErrors: Record<string, string> = {}

    const tax_rate = parseFloat(fd.get('tax_rate') as string)
    if (isNaN(tax_rate) || tax_rate < 0 || tax_rate > 100) {
      newErrors.tax_rate = 'La tasa debe ser un número entre 0 y 100.'
    }
    if (!fd.get('tax_name')) newErrors.tax_name = 'El nombre del impuesto es obligatorio.'
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }
    setErrors({})

    const payload: Record<string, unknown> = {
      currency: fd.get('currency') as string,
      tax_name: fd.get('tax_name') as string,
      tax_rate: fd.get('tax_rate') as string,
      is_configured: true,
    }
    // Include resolución fields if present (Chile)
    const resNum = fd.get('resolucion_number') as string | null
    if (resNum !== null) payload.resolucion_number = resNum
    payload.resolucion_date = resDate || null
    // Include acteco if present (Chile)
    const acteco = fd.get('acteco') as string | null
    if (acteco !== null) payload.acteco = acteco
    // Include rut_envia if present (Chile)
    const rutEnvia = fd.get('rut_envia') as string | null
    if (rutEnvia !== null) payload.rut_envia = rutEnvia
    // Include sii_office if present (Chile)
    const siiOffice = fd.get('sii_office') as string | null
    if (siiOffice !== null) payload.sii_office = siiOffice
    // Auto-distribución del DTE al receptor (Chile)
    if (loc?.country_code === 'CL') {
      payload.auto_distribute_dte = fd.get('auto_distribute_dte') === 'on'
      payload.auto_import_rcv_matched_dte = fd.get('auto_import_rcv_matched_dte') === 'on'
      payload.allow_approve_without_xml = fd.get('allow_approve_without_xml') === 'on'
      payload.auto_send_cesion = fd.get('auto_send_cesion') === 'on'
      // PPM (Pago Provisional Mensual, código 062 del F29)
      payload.regimen_tributario = (fd.get('regimen_tributario') as string) ?? ''
      payload.ppm_modo = (fd.get('ppm_modo') as string) || 'manual'
      const ppmTasa = fd.get('ppm_tasa') as string | null
      payload.ppm_tasa = ppmTasa === null || ppmTasa.trim() === '' ? null : ppmTasa
    }
    // Vista 360 de Compras (independiente del país)
    payload.ui_purchases_360 = fd.get('ui_purchases_360') === 'on'
    // Mutual de seguridad (Chile)
    const mutual = fd.get('mutual') as string | null
    if (mutual !== null) payload.mutual = mutual
    const mutualRate = fd.get('mutual_rate') as string | null
    if (mutualRate !== null) payload.mutual_rate = mutualRate
    const ccaf = fd.get('ccaf_code') as string | null
    if (ccaf !== null) payload.ccaf_code = ccaf
    // email_intercambio fields are read-only (auto-generated)
    // Additional taxes managed via state
    payload.additional_taxes = additionalTaxes

    update(payload as Partial<import('@/modules/config/hooks/useConfig').LocalizationConfig>, {
      onSuccess: () => showToast('Localización guardada correctamente.', 'success'),
      onError: (err: unknown) => {
        // El backend responde { error: { message, details: { campo: [msg] } } }.
        // Antes solo se leía data.rut_envia (ruta y campo equivocados) → cualquier
        // otro error de validación (mutual, tasa PPM, régimen, etc.) caía al
        // mensaje genérico y el usuario no sabía qué campo corregir.
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors) setErrors(p => ({ ...p, ...fieldErrors }))
        const firstFieldMsg = fieldErrors ? Object.values(fieldErrors)[0] : null
        showToast(
          firstFieldMsg || extractErrorMessage(err, 'Error al guardar. Intenta nuevamente.'),
          'error',
        )
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-content-subtle" size={28} />
      </div>
    )
  }

  const CURRENCIES = [
    { value: 'CLP', label: 'Peso Chileno (CLP)' },
    { value: 'PEN', label: 'Sol Peruano (PEN)' },
    { value: 'ARS', label: 'Peso Argentino (ARS)' },
    { value: 'MXN', label: 'Peso Mexicano (MXN)' },
    { value: 'USD', label: 'Dólar Americano (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl [&>section:not(:first-of-type)]:pt-8 [&>section:not(:first-of-type)]:border-t [&>section:not(:first-of-type)]:border-divider">
      {/* Banner de estado */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border text-sm
        ${loc?.is_configured
          ? 'bg-success-surface border-success-border text-success-content-strong'
          : 'bg-warning-surface border-warning-border text-warning-content-strong'}`}>
        {loc?.is_configured
          ? <CheckCircle2 size={18} className="text-success-content-soft shrink-0" />
          : <AlertCircle size={18} className="text-warning-content-soft shrink-0" />}
        <div>
          <p className="font-medium">
            {loc?.is_configured ? 'Localización configurada' : 'Localización pendiente'}
          </p>
          <p className="text-xs mt-0.5 opacity-75">
            {loc?.is_configured
              ? `${loc.country_display} · ${loc.currency} · ${loc.tax_name} ${loc.tax_rate}%`
              : 'Completa la configuración para poder emitir documentos de venta.'}
          </p>
        </div>
      </div>

      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">País y moneda</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="País">
            <input value={loc?.country_display || '—'} disabled
              className={`${inputCls} bg-surface-muted text-content-muted cursor-not-allowed`} />
            <p className="mt-1 text-xs text-content-subtle">El país es asignado por el administrador de la plataforma.</p>
          </FormField>
          <FormField label="Moneda principal">
            <select name="currency" defaultValue={loc?.currency || 'CLP'} className={inputCls}>
              {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </FormField>
        </div>
      </section>

      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Impuesto principal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Nombre del impuesto" required error={errors.tax_name}
            hint="Ej: IVA, IGV, IVA MX">
            <input name="tax_name" defaultValue={loc?.tax_name || 'IVA'} placeholder="IVA" className={inputCls} />
          </FormField>
          <FormField label="Tasa (%)" required error={errors.tax_rate}
            hint="Porcentaje sobre el precio neto (ej: 19)">
            <input name="tax_rate" type="number" step="0.01" min="0" max="100"
              defaultValue={loc?.tax_rate || '19.00'} placeholder="19" className={inputCls} />
          </FormField>
        </div>
      </section>

      {loc?.country_code === 'CL' && (
        <section>
          <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Resolución SII y Firmante</h3>
          <p className="text-xs text-content-muted mb-3">Datos de la resolución del SII que autoriza la emisión electrónica y RUT del firmante. Requeridos para el envío de DTEs.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Número de Resolución" hint="NroResol (ej: 15, 80)">
              <input name="resolucion_number" defaultValue={loc?.resolucion_number || ''} placeholder="15" className={inputCls} />
            </FormField>
            <FormField label="Fecha de Resolución" hint="FchResol (ej: 2006-02-01)">
              <DatePicker value={resDate} onChange={setResDate} />
            </FormField>
            <FormField label="RUT del Firmante" error={errors.rut_envia}
              hint="RUT de la persona que firma los DTEs (ej: 9.788.569-9)">
              <input name="rut_envia" defaultValue={loc?.rut_envia ? formatRut(loc.rut_envia) : ''} placeholder="9.788.569-9" className={inputCls} />
            </FormField>
            <FormField label="Actividad Económica (Acteco)"
              hint="Código registrado en sii.cl (ej: 620200)">
              <input name="acteco" defaultValue={loc?.acteco || ''} placeholder="620200" className={inputCls} />
            </FormField>
            <FormField label="Unidad SII"
              hint="Unidad del SII según la dirección de la casa matriz (ej: CHILLAN, CONCEPCION). Aparece en el recuadro del PDF.">
              <input name="sii_office" defaultValue={loc?.sii_office || ''} placeholder="SANTIAGO CENTRO" className={inputCls} />
            </FormField>
          </div>
          <div className="mt-4 p-3 rounded-lg border border-divider bg-soft">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="auto_distribute_dte"
                defaultChecked={loc?.auto_distribute_dte ?? true}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-content">Distribución automática del DTE al receptor</div>
                <div className="text-xs text-content-muted mt-0.5">
                  Cuando el SII acepta un DTE, Clobi envía automáticamente el XML firmado + PDF al
                  email de intercambio del receptor (consultado en el padrón SII de contribuyentes
                  electrónicos, con fallback al <code>CorreoRecep</code> del XML o al email del cliente).
                  Exigido por Res. Ex. SII N°45/2003 y Ley 19.983. Desactivá solo si la distribución
                  se hace desde otro sistema.
                </div>
              </div>
            </label>
          </div>
          <div className="mt-4 p-3 rounded-lg border border-divider bg-soft">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="auto_import_rcv_matched_dte"
                defaultChecked={loc?.auto_import_rcv_matched_dte ?? false}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-content">Auto-importar DTEs que cuadran con RCV</div>
                <div className="text-xs text-content-muted mt-0.5">
                  Al recibir un DTE por correo, si converge con una factura ya registrada en el RCV
                  (estado “Pendiente XML”) y los montos coinciden, Clobi lo importa automáticamente a
                  Compras (crea la factura aprobada y su comprobante en borrador). El acuse/reclamo
                  ante el SII sigue siendo manual. Los documentos sin match en el RCV o con montos que
                  no cuadran quedan para revisión manual.
                </div>
              </div>
            </label>
          </div>
          <div className="mt-4 p-3 rounded-lg border border-divider bg-soft">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="allow_approve_without_xml"
                defaultChecked={loc?.allow_approve_without_xml ?? false}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-content">Permitir aprobar facturas de compra sin XML</div>
                <div className="text-xs text-content-muted mt-0.5">
                  Permite aprobar manualmente una factura de proveedor conocida solo por el RCV
                  (estado “Pendiente XML”) para contabilizarla y registrar la cuenta por pagar con
                  los montos del RCV. Si el XML llega después, se asocia y enriquece el detalle sin
                  re-contabilizar. Desactivado: solo se contabiliza con el XML.
                </div>
              </div>
            </label>
          </div>
          <div className="mt-4 p-3 rounded-lg border border-divider bg-soft">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="auto_send_cesion"
                defaultChecked={loc?.auto_send_cesion ?? false}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium text-content">Enviar cesiones (AEC) al RPETC automáticamente</div>
                <div className="text-xs text-content-muted mt-0.5">
                  Al procesar una operación de factoring, envía el AEC firmado al Registro Público
                  Electrónico de Transferencia de Créditos del SII y guarda el identificador de envío
                  y el estado. Desactivado: el AEC se genera y firma, pero el envío al SII queda
                  manual. Actívalo solo tras validar el formato contra el ambiente configurado.
                </div>
              </div>
            </label>
          </div>
        </section>
      )}

      <section>
        <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Interfaz</h3>
        <div className="p-3 rounded-lg border border-divider bg-soft">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="ui_purchases_360"
              defaultChecked={loc?.ui_purchases_360 ?? false}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium text-content">Habilitar Vista 360 de Compras</div>
              <div className="text-xs text-content-muted mt-0.5">
                Muestra la Vista 360 de la factura de proveedor: un workspace con detalle/PDF/XML y un
                panel contextual (proveedor, recepción y SII, relacionados, comprobante, pagos).
                Convive con el modal de detalle clásico. En validación: actívala solo para probar.
              </div>
            </div>
          </label>
        </div>
      </section>

      {loc?.country_code === 'CL' && (
        <section>
          <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Mutual de Seguridad</h3>
          <p className="text-xs text-content-muted mb-3">Mutual contratada por la empresa. Se aplica a todos los empleados como cargo del empleador en la nómina.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Mutual" hint="Organismo de seguridad laboral">
              <select name="mutual" defaultValue={loc?.mutual || ''} className={inputCls}>
                <option value="">Sin Mutual</option>
                <option value="achs">ACHS</option>
                <option value="ist">IST</option>
                <option value="museg">Mutual de Seguridad</option>
                <option value="isl">ISL</option>
              </select>
            </FormField>
            <FormField label="Tasa Mutual (%)" hint="Tasa total que informa la mutual, de cargo del empleador, con el 0,03 % de la Ley SANNA incluido (ej.: 0.93 = 0,90 + 0,03)">
              <input name="mutual_rate" type="number" step="0.01" min="0" max="10"
                defaultValue={loc?.mutual_rate || '0.93'} placeholder="0.93" className={inputCls} />
            </FormField>
            <FormField label="Caja de Compensación (CCAF)" hint="Si la empresa adhiere a una CCAF, el 7% de salud de los afiliados a FONASA se declara en el Previred desglosado (cot_fonasa + cot_ccaf_no_isapre)">
              <select name="ccaf_code" defaultValue={loc?.ccaf_code || ''} className={inputCls}>
                <option value="">Sin CCAF</option>
                <option value="01">Los Andes</option>
                <option value="02">La Araucana</option>
                <option value="03">Los Héroes</option>
                <option value="04">18 de Septiembre</option>
                <option value="06">Gabriela Mistral</option>
              </select>
            </FormField>
          </div>
        </section>
      )}

      {loc?.country_code === 'CL' && (
        <section>
          <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Pago Provisional Mensual (PPM)</h3>
          <p className="text-xs text-content-muted mb-3">
            El PPM (código 062 del F29) es un anticipo mensual del impuesto a la renta. La tasa depende del régimen
            tributario. El Régimen General tiene tasa variable (la fija el F22): usa modo Manual e ingresa la tasa vigente.
            Las Pro-Pyme pueden derivar la tasa automáticamente (incluye la rebaja transitoria 0,125% sep-2025 a dic-2027).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Régimen Tributario" hint="Determina la tasa en modo automático">
              <select name="regimen_tributario" defaultValue={loc?.regimen_tributario || ''} className={inputCls}>
                <option value="">No configurado</option>
                <option value="general">Régimen General (semi-integrado)</option>
                <option value="propyme_general">Pro-Pyme General (14 D N°3)</option>
                <option value="propyme_transparente">Pro-Pyme Transparente (14 D N°8)</option>
              </select>
            </FormField>
            <FormField label="Modo de cálculo" hint="Manual (tasa fija) o automático según régimen">
              <select name="ppm_modo" defaultValue={loc?.ppm_modo || 'manual'} className={inputCls}>
                <option value="manual">Manual (tasa fija)</option>
                <option value="automatico">Automático (según régimen)</option>
              </select>
            </FormField>
            <FormField label="Tasa PPM (%)" hint="Usada en modo Manual. Ej: 0.125">
              <input name="ppm_tasa" type="number" step="0.001" min="0" max="100"
                defaultValue={loc?.ppm_tasa ?? ''} placeholder="0.125" className={inputCls} />
            </FormField>
          </div>
        </section>
      )}

      {loc?.country_code === 'CL' && (
        <section>
          <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-4">Casillas de Intercambio Electrónico</h3>
          <p className="text-xs text-content-muted mb-3">
            Casillas generadas automáticamente para registrar en el SII. Copie estos correos y regístrelos en sii.cl según el ambiente activo.
          </p>

          <div className="mb-4">
            <h4 className="text-xs font-medium text-content-muted mb-2">Producción</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CopyField label="Intercambio Empresas" value={loc?.email_intercambio_empresas || ''} />
              <CopyField label="Intercambio SII" value={loc?.email_intercambio_sii || ''} />
            </div>
          </div>

          <div>
            <h4 className="text-xs font-medium text-content-muted mb-2">Certificación</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CopyField label="Intercambio Empresas" value={loc?.email_intercambio_empresas_cert || ''} />
              <CopyField label="Intercambio SII" value={loc?.email_intercambio_sii_cert || ''} />
            </div>
          </div>

          {/* Casilla de Invoices Internacionales */}
          {loc?.email_intl_invoices && (
            <div className="mt-6 pt-4 border-t border-divider">
              <h4 className="text-xs font-medium text-content-muted mb-2">Invoices Internacionales (partner licenses)</h4>
              <p className="text-xs text-content-subtle mb-2">
                Reenvía a esta casilla los PDFs de tus partners internacionales. El sistema los parseará automáticamente y generará un comprobante contable en borrador.
                Requiere que el módulo "Invoices Internacionales" esté activo en el perfil de empresa.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CopyField label="Casilla expense" value={loc.email_intl_invoices} />
              </div>
            </div>
          )}

          {/* Casilla de Honorarios (BHE Chile) */}
          {loc?.email_honorarios && (
            <div className="mt-6 pt-4 border-t border-divider">
              <h4 className="text-xs font-medium text-content-muted mb-2">Honorarios (BHE del SII)</h4>
              <p className="text-xs text-content-subtle mb-2">
                Configura esta casilla en tu cuenta del SII para recibir las BHE que te emiten contratistas.
                El sistema valida que el RUT receptor coincida con el RUT de tu empresa antes de registrarla.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CopyField label="Casilla BHE" value={loc.email_honorarios} />
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Impuestos Adicionales ──────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-semibold text-content-subtle uppercase tracking-wider">Impuestos Adicionales</h3>
            <p className="text-xs text-content-muted mt-0.5">
              Impuestos configurables que se aplican a productos específicos (ej. ILA para licores y tabacos).
            </p>
          </div>
          <button type="button" onClick={openAddTax}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary-50 transition-colors">
            <Plus size={13} /> Agregar
          </button>
        </div>

        {additionalTaxes.length === 0 ? (
          <div className="flex items-center gap-2.5 p-4 rounded-xl border border-dashed border-divider text-content-subtle text-sm">
            <AlertCircle size={15} className="shrink-0" />
            <span>No hay impuestos adicionales configurados. Haz clic en "Agregar" para añadir uno.</span>
          </div>
        ) : (
          <div className="rounded-xl border border-divider overflow-hidden">
            {/* ⚠️ SIN orden por clic, y es deliberado: estas filas se ordenan a mano
                con las flechas, o sea el orden ES dato del usuario. `moveTax` opera
                sobre el índice del array, así que con la tabla reordenada las
                flechas moverían la fila equivocada. */}
            <DataTable
              rows={additionalTaxes}
              rowKey={(tax, idx) => tax.key || idx}
              minWidth={640}
              caption="Impuestos adicionales configurados"
              columns={[
                {
                  key: 'label', header: 'Nombre',
                  render: tax => (
                    <>
                      <p className="font-medium text-content">{tax.label}</p>
                      {tax.help_text && <p className="text-xs text-content-subtle mt-0.5 line-clamp-1">{tax.help_text}</p>}
                    </>
                  ),
                },
                {
                  key: 'key', header: 'Clave', width: 160,
                  render: tax => (
                    <code className="text-xs bg-surface-muted text-content-muted px-1.5 py-0.5 rounded">{tax.key}</code>
                  ),
                },
                {
                  key: 'rate', header: 'Tasa (%)', width: 110,
                  render: tax => (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-warning-surface text-warning-content border border-warning-border">
                      {tax.rate}%
                    </span>
                  ),
                },
                {
                  key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 120,
                  render: (tax, idx) => (
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => moveTax(idx, -1)} disabled={idx === 0}
                        className="p-1 rounded text-content-subtle hover:text-content-muted disabled:opacity-30 transition-colors" title="Subir" aria-label="Subir impuesto">
                        <ChevronUp size={14} />
                      </button>
                      <button type="button" onClick={() => moveTax(idx, 1)} disabled={idx === additionalTaxes.length - 1}
                        className="p-1 rounded text-content-subtle hover:text-content-muted disabled:opacity-30 transition-colors" title="Bajar" aria-label="Bajar impuesto">
                        <ChevronDown size={14} />
                      </button>
                      <button type="button" onClick={() => openEditTax(idx)}
                        className="p-1 rounded text-content-subtle hover:text-primary transition-colors" title="Editar" aria-label="Editar impuesto">
                        <Pencil size={14} />
                      </button>
                      <button type="button" onClick={() => deleteTax(idx)}
                        className="p-1 rounded text-content-subtle hover:text-danger-solid transition-colors" title="Eliminar" aria-label="Eliminar impuesto">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </section>

      <div className="flex justify-end pt-2">
        <button type="submit" disabled={isPending}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
          {isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {isPending ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </div>

      {toast && <Toast {...toast} />}

      {/* ── Modal: Agregar / Editar impuesto adicional ────────────────────── */}
      {/* ⚠️ Va por PORTAL aunque viva dentro del `<form>` del perfil de empresa, y
          es deliberado: sus campos no llevan `name` y `saveTax` trabaja con estado
          de React, así que el submit de ese form nunca los recogía. Sacarlos evita
          además que un Enter acá dispare el guardado del perfil. */}
      {taxModal.open && (
        <Modal open onClose={closeTaxModal} title={taxModal.idx === null ? 'Nuevo impuesto adicional' : 'Editar impuesto adicional'} size="md" dismissOnScrimClick={false}
          actions={<>
            <button type="button" onClick={closeTaxModal}
              className="px-4 py-2 text-sm text-content-muted hover:text-content rounded-lg hover:bg-soft transition-colors">
              Cancelar
            </button>
            <button type="button" onClick={saveTax}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
              <CheckCircle2 size={14} />
              {taxModal.idx === null ? 'Agregar' : 'Guardar cambios'}
            </button>
          </>}
        >
          <div className="space-y-4">
            <FormField label="Nombre visible" required error={taxErrors.label}
              hint='Ej: "Impuesto Adicional ILA (%)" — se muestra en el formulario de productos'>
              <input
                value={taxModal.draft.label}
                onChange={e => setTaxModal(p => ({ ...p, draft: { ...p.draft, label: e.target.value, key: autoKey(e.target.value) } }))}
                placeholder="Impuesto Adicional ILA (%)"
                className={inputCls}
                autoFocus
              />
            </FormField>
            <FormField label="Clave interna" hint="Se usa en el sistema para identificar el impuesto. Se genera automáticamente del nombre.">
              <input
                value={taxModal.draft.key}
                onChange={e => setTaxModal(p => ({ ...p, draft: { ...p.draft, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') } }))}
                placeholder="ila_rate"
                className={`${inputCls} font-mono`}
              />
            </FormField>
            <FormField label="Descripción de ayuda"
              hint="Texto que verá el usuario al configurar este impuesto en un producto">
              <input
                value={taxModal.draft.help_text}
                onChange={e => setTaxModal(p => ({ ...p, draft: { ...p.draft, help_text: e.target.value } }))}
                placeholder="Aplica solo a licores, tabacos y bebidas analcohólicas..."
                className={inputCls}
              />
            </FormField>
            <FormField label="Tasa (%)" required error={taxErrors.rate}
              hint="Porcentaje fijo que se aplicará al precio neto del producto (ej: 10 para 10%)">
              <input
                type="number" step="0.01" min="0" max="100"
                value={taxModal.draft.rate}
                onChange={e => setTaxModal(p => ({ ...p, draft: { ...p.draft, rate: parseFloat(e.target.value) || 0 } }))}
                placeholder="10"
                className={inputCls}
              />
            </FormField>
          </div>
        </Modal>
      )}
    </form>
  )
}

// ─── CFG-03: Pila de Folios ───────────────────────────────────────────────────

function FoliosTab() {
  const { data: folios, isLoading } = useFolioConfigs()
  const { mutate: updateFolio } = useUpdateFolioConfig()
  const { data: cafs = [], isLoading: cafsLoading } = useCAFList()
  const uploadCAF = useUploadCAF()
  // Track saving state per row (by document_type_code)
  const [saving, setSaving] = useState<Set<number>>(new Set())
  const [saved, setSaved] = useState<Set<number>>(new Set())
  // Local edits for next_folio per row
  const [localFolios, setLocalFolios] = useState<Record<number, string>>({})
  const [localThresholds, setLocalThresholds] = useState<Record<number, string>>({})
  const cafRef = useRef<HTMLInputElement>(null)
  // Que tipo de documento tiene el modal de solicitud abierto, si alguno.
  const [solicitandoFolios, setSolicitandoFolios] = useState<number | null>(null)
  // Solo se consulta cuando el modal esta abierto: la tabla tiene 12 filas y
  // no tiene sentido pedir el estado de todas al cargar la pantalla.
  const { data: estadoFolios } = useEstadoFolios(
    solicitandoFolios ?? 0, solicitandoFolios != null)
  const [cafToast, setCafToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)

  // Build CAF lookup by document_type_code
  const cafMap: Record<number, CAFInfo> = {}
  for (const c of cafs) cafMap[c.document_type_code] = c

  const handleFolioBlur = (folio: FolioConfig) => {
    const rawVal = localFolios[folio.document_type_code]
    if (rawVal === undefined) return  // no change
    const val = parseInt(rawVal, 10)
    if (isNaN(val) || val < 1 || val === folio.next_folio) return

    setSaving(prev => new Set(prev).add(folio.document_type_code))
    updateFolio(
      { code: folio.document_type_code, data: { next_folio: val } },
      {
        onSuccess: () => {
          setSaving(prev => { const s = new Set(prev); s.delete(folio.document_type_code); return s })
          setSaved(prev => {
            const s = new Set(prev).add(folio.document_type_code)
            setTimeout(() => setSaved(p => { const n = new Set(p); n.delete(folio.document_type_code); return n }), 2000)
            return s
          })
          setLocalFolios(prev => { const n = { ...prev }; delete n[folio.document_type_code]; return n })
        },
        onError: () => {
          setSaving(prev => { const s = new Set(prev); s.delete(folio.document_type_code); return s })
        },
      }
    )
  }

  const handleToggleActive = (folio: FolioConfig) => {
    updateFolio({ code: folio.document_type_code, data: { is_active: !folio.is_active } })
  }

  // "Pocos folios" depende del negocio: quien emite 10 notas de debito al ano no
  // necesita el mismo aviso que quien factura 200 al mes. Vacio vuelve al
  // automatico (proporcional al tamano del rango).
  const handleThresholdBlur = (folio: FolioConfig) => {
    const raw = localThresholds[folio.document_type_code]
    if (raw === undefined) return
    const trimmed = raw.trim()
    const next = trimmed === '' ? null : parseInt(trimmed, 10)
    if (next !== null && (isNaN(next) || next < 0)) return
    if (next === folio.alert_threshold) {
      setLocalThresholds(prev => { const n = { ...prev }; delete n[folio.document_type_code]; return n })
      return
    }
    setSaving(prev => new Set(prev).add(folio.document_type_code))
    updateFolio(
      { code: folio.document_type_code, data: { alert_threshold: next } },
      {
        onSuccess: () => {
          setSaving(prev => { const s = new Set(prev); s.delete(folio.document_type_code); return s })
          setLocalThresholds(prev => { const n = { ...prev }; delete n[folio.document_type_code]; return n })
        },
        onError: () => {
          setSaving(prev => { const s = new Set(prev); s.delete(folio.document_type_code); return s })
        },
      }
    )
  }

  const handleToggleAlert = (folio: FolioConfig) => {
    updateFolio({ code: folio.document_type_code, data: { alert_enabled: !folio.alert_enabled } })
  }

  const handleUploadCAF = () => {
    const file = cafRef.current?.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append('caf', file)
    // El tipo de documento sale del propio XML (`<TD>`): el backend lo deriva.
    uploadCAF.mutate(fd, {
      onSuccess: (data) => {
        setCafToast({ msg: data.message || 'CAF cargado.', type: 'success' })
        setTimeout(() => setCafToast(null), 4000)
        if (cafRef.current) cafRef.current.value = ''
      },
      onError: (err: any) => {
        setCafToast({ msg: err?.response?.data?.error?.message || 'Error.', type: 'error' })
        setTimeout(() => setCafToast(null), 4000)
      },
    })
  }

  if (isLoading || cafsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-content-subtle" size={28} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {cafToast && (
        <div className={`fixed top-4 right-4 z-[100] px-4 py-2 rounded-lg text-sm shadow-lg ${
          cafToast.type === 'success' ? 'bg-success-fill text-white' : 'bg-danger-fill text-white'
        }`}>{cafToast.msg}</div>
      )}

      <div className="p-4 bg-glass backdrop-blur-glass border border-divider rounded-card text-sm text-content shadow-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-primary dark:text-primary-hover">Gestión de folios y CAF</p>
            <p className="text-xs mt-0.5 text-content-muted">
              Define el próximo folio a asignar por tipo de DTE y sube los Códigos de Autorización de Folios (CAF) del SII.
              Al emitir con integración directa, el sistema genera el TED usando el CAF correspondiente.
            </p>
          </div>
          {/* 🔴 La carga del CAF vive ACÁ ARRIBA y no al final de la pantalla.
              Estaba debajo de una tabla de 12 filas, o sea había que hacer
              scroll para encontrar la acción principal de la pantalla.
              Y es UN clic: el selector de archivo se abre solo y la subida
              arranca al elegir, sin un segundo botón que confirmar. */}
          <div className="shrink-0">
            <input
              ref={cafRef}
              type="file"
              accept=".xml"
              className="hidden"
              onChange={handleUploadCAF}
            />
            <Button
              variant="primary"
              onClick={() => cafRef.current?.click()}
              disabled={uploadCAF.isPending}
              isPending={uploadCAF.isPending}
              leftIcon={<Upload className="w-4 h-4" />}
              title="El archivo del SII ya trae el tipo de documento: Clobi lo lee de ahí y verifica que el CAF sea del RUT de la empresa antes de guardarlo."
            >
              Subir CAF
            </Button>
          </div>
        </div>
      </div>

      {/* Migrada al DataTable: escrita a mano no tenia ZEBRA, y su encabezado
          era distinto del resto del sistema. Que las celdas lleven campos
          editables no la excluye — el componente pinta lo que se le da, y el
          estado de cada input sigue viviendo en esta pantalla. */}
      <DataTable
        columns={[
          { key: 'code', header: 'Cod.', width: 64,
            render: folio => (
              <span className="inline-flex items-center justify-center w-8 h-6 bg-surface-muted text-content-muted text-xs font-mono rounded">
                {folio.document_type_code}
              </span>
            ) },
          { key: 'label', header: 'Tipo de Documento',
            render: folio => (
              <span className={folio.is_active ? 'text-content' : 'text-content-subtle'}>
                {folio.document_type_label}
              </span>
            ) },
          { key: 'next_folio', header: 'Próximo Folio', width: 112,
            render: folio => {
              const isSaving = saving.has(folio.document_type_code)
              const isSaved = saved.has(folio.document_type_code)
              const currentVal = localFolios[folio.document_type_code] ?? String(folio.next_folio)
              return (
                <div className="flex items-center gap-2">
                  <input aria-label="Próximo Folio"
                    type="number"
                    min={1}
                    value={currentVal}
                    onChange={e => setLocalFolios(prev => ({ ...prev, [folio.document_type_code]: e.target.value }))}
                    onBlur={() => handleFolioBlur(folio)}
                    disabled={!folio.is_active}
                    className="w-24 px-2 py-1 text-xs border border-input-border rounded-md bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 disabled:bg-surface-muted disabled:text-content-subtle disabled:cursor-not-allowed"
                  />
                  {isSaving && <Loader2 size={12} className="animate-spin text-primary" />}
                  {isSaved && <CheckCircle2 size={12} className="text-success" />}
                </div>
              )
            } },
          { key: 'caf', header: 'CAF', width: 112,
            render: folio => {
              const caf = cafMap[folio.document_type_code]
              if (!caf?.has_caf) return <div className="text-center text-xs text-content-subtle">Sin CAF</div>
              const cafs = caf.cafs && caf.cafs.length
                ? caf.cafs
                : [{ valid_from: caf.caf_valid_from!, valid_to: caf.caf_valid_to!, gaps: [], gaps_count: 0, total: 0, disponibles: 0 }]
              const gapsTitle = cafs
                .filter(c => c.gaps_count > 0)
                .map(c => `${c.valid_from}-${c.valid_to}: ${c.gaps.map(g => g.from === g.to ? `${g.from}` : `${g.from}-${g.to}`).join(', ')}`)
                .join(' | ')
              return (
                <div className="flex flex-col items-center gap-1">
                  <div className="flex flex-wrap justify-center gap-1">
                    {cafs.map((c, i) => (
                      <span key={i} className="text-xs bg-success-surface-strong text-success-content px-2 py-0.5 rounded-full font-mono">
                        {c.valid_from}-{c.valid_to}
                      </span>
                    ))}
                  </div>
                  {(caf.gaps_count ?? 0) > 0 && (
                    <span
                      className="text-[10px] text-warning-content-soft font-medium cursor-help"
                      title={`Folios no utilizados (anulables en SII) - ${gapsTitle}`}
                    >
                      {caf.gaps_count} salto{caf.gaps_count === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )
            } },
          { key: 'disponibles', header: 'Disponibles', width: 128,
            render: folio => {
              const caf = cafMap[folio.document_type_code]
              if (!caf?.has_caf || caf.folios_disponibles == null) {
                return <div className="text-center text-xs text-content-subtle">-</div>
              }
              const pct = caf.folios_total ? Math.round(caf.folios_disponibles / caf.folios_total * 100) : 0
              const isLow = caf.alert
              const isZero = caf.folios_disponibles === 0
              return (
                <div className="flex flex-col items-center gap-0.5">
                  <span className={`text-xs font-mono font-bold ${isZero ? 'text-danger-content-soft' : isLow ? 'text-warning-content-soft' : 'text-content'}`}>
                    {caf.folios_disponibles.toLocaleString()}
                  </span>
                  <div className="w-16 h-1.5 bg-surface-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isZero ? 'bg-danger-solid' : isLow ? 'bg-warning-solid' : 'bg-success-solid'}`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  {isLow && !isZero && <span className="text-[10px] text-warning-content-soft font-medium">Pocos</span>}
                  {isZero && <span className="text-[10px] text-danger-content-soft font-bold">Agotados</span>}
                  {(caf.folios_en_terminales ?? 0) > 0 && (
                    <span className="text-[10px] text-content-subtle"
                      title="Entregados a las cajas del punto de venta y todavía sin usar. Siguen siendo de la empresa, pero solo esas cajas pueden usarlos.">
                      +{caf.folios_en_terminales!.toLocaleString()} en cajas
                    </span>
                  )}
                </div>
              )
            } },
          { key: 'threshold', header: 'Avisar cuando queden', width: 144,
            render: folio => {
              const caf = cafMap[folio.document_type_code]
              const auto = caf?.alert_threshold_efectivo
              const raw = localThresholds[folio.document_type_code]
              const value = raw !== undefined
                ? raw
                : (folio.alert_threshold != null ? String(folio.alert_threshold) : '')
              return (
                <div className="flex items-center justify-center gap-1.5">
                  <input aria-label="Avisar cuando queden"
                    type="number"
                    min={0}
                    value={value}
                    placeholder={auto != null ? `auto: ${auto}` : 'auto'}
                    title={folio.alert_threshold == null && auto != null
                      ? `Automatico: avisa cuando queden ${auto} folios. Escribe un numero para fijarlo.`
                      : 'Avisa cuando queden esta cantidad de folios. Borralo para volver al automatico.'}
                    onChange={e => setLocalThresholds(prev => ({ ...prev, [folio.document_type_code]: e.target.value }))}
                    onBlur={() => handleThresholdBlur(folio)}
                    disabled={!folio.is_active || !folio.alert_enabled}
                    className="w-20 px-2 py-1 text-xs text-center border border-input-border rounded-md bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 disabled:bg-surface-muted disabled:text-content-subtle disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={() => handleToggleAlert(folio)}
                    title={folio.alert_enabled
                      ? 'Avisos activados. Desactivalos para dejar de recibir el aviso de este tipo de documento (quedarse SIN folios se avisa igual).'
                      : 'Avisos desactivados para este tipo de documento.'}
                    className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                      folio.alert_enabled
                        ? 'border-divider text-content-muted hover:bg-soft'
                        : 'border-warning-border text-warning-content bg-warning-surface'
                    }`}
                  >
                    {folio.alert_enabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              )
            } },
          { key: 'activo', header: 'Activo', width: 80,
            render: folio => (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => handleToggleActive(folio)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1
                    ${folio.is_active ? 'bg-primary' : 'bg-surface-muted'}`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface shadow transition-transform
                      ${folio.is_active ? 'translate-x-4.5' : 'translate-x-0.5'}`}
                  />
                </button>
              </div>
            ) },
          { key: 'sii', header: 'Folios SII', width: 96,
            render: folio => (
              <div className="text-center">
                {/* Abre el modal, no pide folios: pedirlos es irreversible y la
                    confirmacion es de la persona, no de un clic. */}
                <button
                  type="button"
                  onClick={() => setSolicitandoFolios(folio.document_type_code)}
                  disabled={!folio.is_active}
                  title={folio.is_active
                    ? 'Consultar al SII cuantos folios autoriza y solicitarlos'
                    : 'Activa el tipo de documento para poder pedirle folios al SII'}
                  className="text-xs px-2 py-1 rounded-md border border-divider text-content-muted hover:bg-soft hover:text-content disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Pedir al SII
                </button>
              </div>
            ) },
        ]}
        rows={folios || []}
        rowKey={folio => folio.document_type_code}
        minWidth={896}
      />

      {solicitandoFolios != null && (
        <SolicitarFoliosModal
          documentTypeCode={solicitandoFolios}
          documentTypeLabel={
            folios?.find(f => f.document_type_code === solicitandoFolios)
              ?.document_type_label ?? `Tipo ${solicitandoFolios}`
          }
          estado={estadoFolios}
          onClose={() => setSolicitandoFolios(null)}
        />
      )}
    </div>
  )
}

// ─── CFG-04: Campos Personalizados Globales ───────────────────────────────────

const TIPO_LABELS: Record<GlobalCustomField['tipo'], string> = {
  string: 'Texto',
  numero: 'Número',
  fecha: 'Fecha',
  select: 'Menú desplegable',
  multiselect: 'Selección múltiple',
  boolean: 'Casilla de verificación',
  entity_link: 'Vínculo a entidad',
}

const LINK_ENTITY_LABELS: Record<string, string> = {
  customer: 'Cliente',
  sale_document: 'Documento de venta',
  supplier_invoice: 'Documento de compra',
}

const SCOPE_TABS: { value: 'ventas' | 'compras' | 'contratos'; label: string }[] = [
  { value: 'ventas', label: 'Ventas' },
  { value: 'compras', label: 'Compras' },
  { value: 'contratos', label: 'Contratos' },
]

/** Los tipos que llevan una lista de opciones (#1752: «Selección múltiple» también). */
const conOpciones = (tipo: string) => tipo === 'select' || tipo === 'multiselect'

function CustomFieldsTab() {
  const [scope, setScope] = useState<'ventas' | 'compras' | 'contratos'>('ventas')
  const { data: fields = [], isLoading } = useGlobalCustomFields(false, scope)
  const { mutate: create, isPending: isCreating } = useCreateGlobalCustomField()
  const { mutate: update } = useUpdateGlobalCustomField()
  const { mutate: remove } = useDeleteGlobalCustomField()

  // Form state for new field
  const [newNombre, setNewNombre] = useState('')
  const [newTipo, setNewTipo] = useState<GlobalCustomField['tipo']>('string')
  const [newLinkEntity, setNewLinkEntity] = useState<GlobalCustomField['link_entity']>('customer')
  const [newRequired, setNewRequired] = useState(false)
  const [newDefault, setNewDefault] = useState('')
  const [newOpciones, setNewOpciones] = useState<string[]>([])
  const [newOpcionInput, setNewOpcionInput] = useState('')
  const [formError, setFormError] = useState('')

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editNombre, setEditNombre] = useState('')
  const [editTipo, setEditTipo] = useState<GlobalCustomField['tipo']>('string')
  const [editLinkEntity, setEditLinkEntity] = useState<GlobalCustomField['link_entity']>('customer')
  const [editDefault, setEditDefault] = useState('')
  const [editOpciones, setEditOpciones] = useState<string[]>([])
  const [editOpcionInput, setEditOpcionInput] = useState('')

  // Confirmación de borrado
  const { confirm: confirmarBorrado, modalProps: modalBorrado } = useConfirmModal()

  const handleAdd = () => {
    if (!newNombre.trim()) { setFormError('El nombre es obligatorio.'); return }
    if (conOpciones(newTipo) && newOpciones.length < 1) { setFormError('Agrega al menos una opción.'); return }
    if (newTipo === 'entity_link' && !newLinkEntity) { setFormError('Elegir la entidad objetivo.'); return }
    setFormError('')
    create(
      {
        nombre: newNombre.trim(), tipo: newTipo, scope,
        link_entity: newTipo === 'entity_link' ? newLinkEntity : '',
        is_required: newRequired, usable_in_filters: false,
        default_value: newTipo === 'entity_link' ? '' : newDefault,
        opciones: conOpciones(newTipo) ? newOpciones : [], order: fields.length, is_active: true,
      },
      {
        onSuccess: () => {
          setNewNombre(''); setNewTipo('string'); setNewLinkEntity('customer'); setNewRequired(false); setNewDefault(''); setNewOpciones([]); setNewOpcionInput('')
        },
      }
    )
  }

  const handleStartEdit = (f: GlobalCustomField) => {
    setEditingId(f.id)
    setEditNombre(f.nombre)
    setEditTipo(f.tipo)
    setEditLinkEntity(f.link_entity || 'customer')
    setEditDefault(f.default_value)
    setEditOpciones(f.opciones || [])
    setEditOpcionInput('')
  }

  const handleSaveEdit = (f: GlobalCustomField) => {
    update({ id: f.id, data: {
      nombre: editNombre, tipo: editTipo,
      link_entity: editTipo === 'entity_link' ? editLinkEntity : '',
      default_value: editTipo === 'entity_link' ? '' : editDefault,
      opciones: conOpciones(editTipo) ? editOpciones : [],
    } })
    setEditingId(null)
  }

  const handleMoveUp = (f: GlobalCustomField) => {
    const idx = fields.findIndex(x => x.id === f.id)
    if (idx <= 0) return
    const prev = fields[idx - 1]
    update({ id: f.id, data: { order: prev.order } })
    update({ id: prev.id, data: { order: f.order } })
  }

  const handleMoveDown = (f: GlobalCustomField) => {
    const idx = fields.findIndex(x => x.id === f.id)
    if (idx < 0 || idx >= fields.length - 1) return
    const next = fields[idx + 1]
    update({ id: f.id, data: { order: next.order } })
    update({ id: next.id, data: { order: f.order } })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-content-subtle" size={28} />
      </div>
    )
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className="p-4 bg-glass backdrop-blur-glass border border-divider rounded-card text-sm text-content shadow-card">
        <p className="font-medium text-primary dark:text-primary-hover">Campos de cabecera globales</p>
        <p className="text-xs mt-0.5 opacity-75">
          Estos campos aparecen automáticamente en el encabezado de cada documento del módulo seleccionado.
          Los campos obligatorios (*) deben ser completados antes de emitir/contabilizar.
        </p>
      </div>

      {/* Selector de módulo (scope) */}
      <div className="inline-flex rounded-lg border border-divider bg-glass-3 p-1">
        {SCOPE_TABS.map(t => (
          <button key={t.value} type="button" onClick={() => { setScope(t.value); setEditingId(null) }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              scope === t.value ? 'bg-primary text-on-primary' : 'text-content-muted hover:text-content'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Formulario para nuevo campo */}
      <div className="border border-divider rounded-xl p-4 bg-glass-3">
        <p className="text-xs font-semibold text-content-muted uppercase tracking-wider mb-3">Agregar nuevo campo</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label htmlFor="configpage-nombre-2" className="block text-xs font-medium text-content-muted mb-1">
              Nombre <span className="text-danger-solid">*</span>
            </label>
            <input id="configpage-nombre-2"
              value={newNombre}
              onChange={e => { setNewNombre(e.target.value); setFormError('') }}
              placeholder="Ej: OrdenDeCompra"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
            />
            {formError && <p className="mt-1 text-xs text-danger-solid">{formError}</p>}
          </div>
          <div>
            <label htmlFor="configpage-tipo" className="block text-xs font-medium text-content-muted mb-1">Tipo</label>
            <select id="configpage-tipo"
              value={newTipo}
              onChange={e => { setNewTipo(e.target.value as GlobalCustomField['tipo']); setNewDefault(''); if (!conOpciones(e.target.value)) setNewOpciones([]) }}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
            >
              <option value="string">Texto</option>
              <option value="numero">Número</option>
              <option value="fecha">Fecha</option>
              <option value="select">Menú desplegable</option>
              <option value="multiselect">Selección múltiple</option>
              <option value="boolean">Casilla de verificación</option>
              <option value="entity_link">Vínculo a entidad</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">
              {newTipo === 'entity_link' ? 'Entidad objetivo' : 'Valor por defecto'}
            </label>
            {newTipo === 'entity_link' ? (
              <select aria-label="Entidad objetivo"
                value={newLinkEntity}
                onChange={e => setNewLinkEntity(e.target.value as GlobalCustomField['link_entity'])}
                className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
              >
                <option value="customer">Cliente</option>
                <option value="sale_document">Documento de venta</option>
                <option value="supplier_invoice">Documento de compra</option>
              </select>
            ) : newTipo === 'boolean' ? (
              <select aria-label="Valor por defecto"
                value={newDefault}
                onChange={e => setNewDefault(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
              >
                <option value="">Sin valor por defecto</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            ) : newTipo === 'select' ? (
              <select aria-label="Valor por defecto"
                value={newDefault}
                onChange={e => setNewDefault(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
              >
                <option value="">Sin valor por defecto</option>
                {newOpciones.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input aria-label="Valor por defecto"
                value={newDefault}
                onChange={e => setNewDefault(e.target.value)}
                placeholder="Opcional"
                className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
              />
            )}
          </div>
        </div>
        {conOpciones(newTipo) && (
          <div className="mt-3 p-3 bg-glass backdrop-blur-glass border border-divider rounded-card shadow-card">
            <p className="text-xs font-medium text-content-muted mb-2">Opciones del menú desplegable</p>
            <div className="flex gap-2 mb-2">
              <input aria-label="Nueva opción del menú desplegable"
                value={newOpcionInput}
                onChange={e => setNewOpcionInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = newOpcionInput.trim(); if (v && !newOpciones.includes(v)) { setNewOpciones([...newOpciones, v]); setNewOpcionInput('') } } }}
                placeholder="Escribir una opción y presionar Enter…"
                className="flex-1 px-3 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
              />
              <button
                type="button"
                onClick={() => { const v = newOpcionInput.trim(); if (v && !newOpciones.includes(v)) { setNewOpciones([...newOpciones, v]); setNewOpcionInput('') } }}
                className="px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover transition-colors"
              >
                <Plus size={13} />
              </button>
            </div>
            {newOpciones.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {newOpciones.map((opt, oi) => (
                  <span key={oi} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-primary-50 text-primary-hover rounded-full border border-primary-200">
                    {opt}
                    <button type="button" onClick={() => setNewOpciones(newOpciones.filter((_, j) => j !== oi))} className="text-primary hover:text-danger-solid"><X size={11} /></button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-content-subtle italic">Sin opciones aún.</p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-3">
          <label className="inline-flex items-center gap-2 text-sm text-content-muted cursor-pointer">
            <input
              type="checkbox"
              checked={newRequired}
              onChange={e => setNewRequired(e.target.checked)}
              className="h-4 w-4 rounded border-divider-strong text-primary focus:ring-primary"
            />
            Campo obligatorio
          </label>
          <button
            type="button"
            onClick={handleAdd}
            disabled={isCreating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
          >
            {isCreating ? <Loader2 size={13} className="animate-spin" /> : <ListPlus size={14} />}
            Agregar campo
          </button>
        </div>
      </div>

      {/* Tabla de campos */}
      {fields.length === 0 ? (
        <p className="text-sm text-content-subtle italic py-4 text-center">Sin campos definidos. Agrega el primero con el botón de arriba.</p>
      ) : (
        // Migrada al DataTable. La confirmación de borrado reemplazaba la fila
        // entera por un `<td colSpan={7}>` —el componente emite una celda por
        // columna— y ahora es un `ConfirmModal`. La edición en línea sigue igual:
        // cada columna dibuja el campo cuando la fila está en edición.
        //
        // ⚠️ La posición viaja DENTRO del dato (`f.idx`), no como el índice que
        // recibe `render`: los botones de subir/bajar dependen de ella, y ese
        // índice es el de la fila ya ordenada (ver REVISION_DS_TABLAS §5).
        <DataTable<GlobalCustomField & { idx: number }>
          caption="Campos personalizados"
          rows={fields.map((f, idx) => ({ ...f, idx }))}
          rowKey={f => f.id}
          columns={[
            {
              key: 'nombre', header: 'Nombre',
              render: f => editingId === f.id ? (
            <input aria-label={`Nombre — campo ${f.nombre}`}
              value={editNombre}
              onChange={e => setEditNombre(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            ) : (
            <span className="text-sm text-content">
              {f.nombre}
              {f.is_required && <span className="ml-1 text-danger-solid text-xs">*</span>}
            </span>
            ),
            },
            {
              key: 'tipo', header: 'Tipo', width: 96,
              render: f => editingId === f.id ? (
            <>
            <select aria-label={`Tipo — campo ${f.nombre}`}
              value={editTipo}
              onChange={e => { setEditTipo(e.target.value as GlobalCustomField['tipo']); if (!conOpciones(e.target.value)) setEditOpciones([]) }}
              className="w-full px-2 py-1 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="string">Texto</option>
              <option value="numero">Número</option>
              <option value="fecha">Fecha</option>
              <option value="select">Menú desplegable</option>
              <option value="multiselect">Selección múltiple</option>
              <option value="boolean">Casilla de verificación</option>
              <option value="entity_link">Vínculo a entidad</option>
            </select>
            {editTipo === 'entity_link' && (
              <select aria-label={`Entidad objetivo — campo ${f.nombre}`}
                value={editLinkEntity}
                onChange={e => setEditLinkEntity(e.target.value as GlobalCustomField['link_entity'])}
                className="mt-1.5 w-full px-2 py-1 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="customer">Cliente</option>
                <option value="sale_document">Documento de venta</option>
                <option value="supplier_invoice">Documento de compra</option>
              </select>
            )}
            {conOpciones(editTipo) && (
              <div className="mt-1.5 space-y-1">
                <div className="flex gap-1">
                  <input aria-label={`Nueva opción — campo ${f.nombre}`}
                    value={editOpcionInput}
                    onChange={e => setEditOpcionInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = editOpcionInput.trim(); if (v && !editOpciones.includes(v)) { setEditOpciones([...editOpciones, v]); setEditOpcionInput('') } } }}
                    placeholder="Nueva opción…"
                    className="flex-1 px-2 py-0.5 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button type="button" onClick={() => { const v = editOpcionInput.trim(); if (v && !editOpciones.includes(v)) { setEditOpciones([...editOpciones, v]); setEditOpcionInput('') } }} className="px-1.5 py-0.5 text-xs bg-primary text-on-primary rounded-md hover:bg-primary-hover"><Plus size={11} /></button>
                </div>
                {editOpciones.map((opt, oi) => (
                  <span key={oi} className="inline-flex items-center gap-0.5 mr-1 px-2 py-0.5 text-xs bg-primary-50 text-primary-hover rounded-full border border-primary-200">
                    {opt}
                    <button type="button" onClick={() => setEditOpciones(editOpciones.filter((_, j) => j !== oi))} className="ml-0.5 text-primary hover:text-danger-solid"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            </>
            ) : (
            <>
            <span className="text-xs text-content-muted">{TIPO_LABELS[f.tipo]}</span>
            {f.tipo === 'entity_link' && f.link_entity && (
              <span className="ml-1 inline-block px-1.5 py-0 text-[10px] bg-primary-50 text-primary-hover rounded-full border border-primary-200">
                → {LINK_ENTITY_LABELS[f.link_entity] || f.link_entity}
              </span>
            )}
            {conOpciones(f.tipo) && f.opciones && f.opciones.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-0.5">
                {f.opciones.map((opt, oi) => (
                  <span key={oi} className="inline-block px-1.5 py-0 text-[10px] bg-surface-muted text-content-muted rounded-full">{opt}</span>
                ))}
              </div>
            )}
            </>
            ),
            },
            {
              key: 'obligatorio', header: 'Obligatorio', width: 96,
              render: f => editingId === f.id ? (
            <div className="text-center">
            <span className="text-xs text-content-subtle">—</span>
            </div>
            ) : (
            <div className="text-center">
            <input
              type="checkbox"
              aria-label={`Obligatorio — campo ${f.nombre}`}
              checked={f.is_required}
              onChange={e => update({ id: f.id, data: { is_required: e.target.checked } })}
              className="h-3.5 w-3.5 rounded border-divider-strong text-primary focus:ring-primary cursor-pointer"
            />
            </div>
            ),
            },
            {
              key: 'filtro', header: <span title="Disponible como filtro en el listado">Filtro</span>, width: 80,
              render: f => editingId === f.id ? (
            <div className="text-center">
            <span className="text-xs text-content-subtle">—</span>
            </div>
            ) : (
            <div className="text-center">
            <input
              type="checkbox"
              aria-label={`Usable en filtros — campo ${f.nombre}`}
              checked={f.usable_in_filters}
              onChange={e => update({ id: f.id, data: { usable_in_filters: e.target.checked } })}
              className="h-3.5 w-3.5 rounded border-divider-strong text-primary focus:ring-primary cursor-pointer"
              title="Disponible como filtro en el listado"
            />
            </div>
            ),
            },
            {
              key: 'defecto', header: 'Valor por defecto',
              render: f => editingId === f.id ? (
            <>
            {editTipo === 'boolean' ? (
              <select aria-label={`Valor por defecto — campo ${f.nombre}`}
                value={editDefault}
                onChange={e => setEditDefault(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Sin valor por defecto</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            ) : editTipo === 'select' ? (
              <select aria-label={`Valor por defecto — campo ${f.nombre}`}
                value={editDefault}
                onChange={e => setEditDefault(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Sin valor por defecto</option>
                {editOpciones.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input aria-label={`Valor por defecto — campo ${f.nombre}`}
                value={editDefault}
                onChange={e => setEditDefault(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-divider-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            )}
            </>
            ) : (
            <span className="text-xs text-content-muted">
              {f.tipo === 'boolean' ? (f.default_value === 'true' ? 'Sí' : f.default_value === 'false' ? 'No' : '—') : (f.default_value || '—')}
            </span>
            ),
            },
            {
              key: 'activo', header: 'Activo', width: 80,
              render: f => editingId === f.id ? null : (
            <div className="text-center">
            <button
              type="button"
              onClick={() => update({ id: f.id, data: { is_active: !f.is_active } })}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1
                ${f.is_active ? 'bg-primary' : 'bg-surface-muted'}`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface shadow transition-transform
                  ${f.is_active ? 'translate-x-4.5' : 'translate-x-0.5'}`}
              />
            </button>
            </div>
            ),
            },
            {
              key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 128,
              render: f => editingId === f.id ? (
            <div className="flex items-center justify-end gap-1">
              <button
                onClick={() => handleSaveEdit(f)}
                className="px-2 py-1 text-xs bg-primary text-on-primary rounded-md hover:bg-primary-hover transition-colors"
              >
                Guardar
              </button>
              <button
                onClick={() => setEditingId(null)}
                className="px-2 py-1 text-xs border border-divider rounded-md hover:bg-soft transition-colors"
              >
                Cancelar
              </button>
            </div>
            ) : (
            <div className="flex items-center justify-end gap-1">
              <button
                onClick={() => handleMoveUp(f)}
                disabled={f.idx === 0}
                className="p-1 text-content-subtle hover:text-content-muted disabled:opacity-20 transition-colors"
                title="Subir"
                aria-label="Subir campo"
              >
                <ChevronUp size={13} />
              </button>
              <button
                onClick={() => handleMoveDown(f)}
                disabled={f.idx === fields.length - 1}
                className="p-1 text-content-subtle hover:text-content-muted disabled:opacity-20 transition-colors"
                title="Bajar"
                aria-label="Bajar campo"
              >
                <ChevronDown size={13} />
              </button>
              <button
                onClick={() => handleStartEdit(f)}
                className="p-1 text-content-subtle hover:text-primary transition-colors"
                title="Editar"
                aria-label="Editar campo"
              >
                <Pencil size={13} />
              </button>
              <button
                onClick={() => confirmarBorrado({
              message: `¿Eliminar el campo «${f.nombre}»?`,
              variant: 'danger',
              confirmLabel: 'Eliminar',
              onConfirm: () => remove(f.id),
            })}
                className="p-1 text-content-subtle hover:text-danger-content-soft transition-colors"
                title="Eliminar"
                aria-label="Eliminar campo"
              >
                <Trash2 size={13} />
              </button>
            </div>
            ),
            },
          ]}
        />
      )}
      <ConfirmModal {...modalBorrado} />
    </div>
  )
}

function LocalizationTabWithCert() {
  return <LocalizationTab />
}

// ─── Pasarelas de Pago Tab ─────────────────────────────────────────────────

const GATEWAY_INFO: Record<string, { name: string; description: string; color: string; fields: Array<{ key: string; label: string; type?: string }> }> = {
  stripe: {
    name: 'Stripe',
    description: 'Pagos con tarjeta internacional. Compatible con todos los países.',
    color: 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800',
    fields: [
      { key: 'public_key', label: 'Clave Pública (pk_...)' },
      { key: 'secret_key', label: 'Clave Secreta (sk_...)', type: 'password' },
      { key: 'webhook_secret', label: 'Webhook Secret (whsec_...)', type: 'password' },
    ],
  },
  flow: {
    name: 'Flow',
    description: 'Pasarela chilena. Soporta tarjetas, WebPay y transferencias.',
    color: 'bg-primary-50 dark:bg-primary-900/30 border-primary-200 dark:border-primary-800',
    fields: [
      { key: 'api_key', label: 'API Key' },
      { key: 'secret_key', label: 'Secret Key', type: 'password' },
    ],
  },
  payku: {
    name: 'Payku',
    description: 'Pasarela chilena. Soporta WebPay Plus y suscripciones.',
    color: 'bg-success-surface border-success-border',
    fields: [
      { key: 'public_token', label: 'Public Token' },
      { key: 'private_token', label: 'Private Token', type: 'password' },
    ],
  },
}

function GatewayCard({ gateway }: { gateway: PaymentGateway }) {
  const { mutate: updateGateway, isPending: updating } = useUpdateGateway()
  const { mutate: testGateway, isPending: testing, data: testResult } = useTestGateway()
  const info = GATEWAY_INFO[gateway.gateway_type]
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [webhookSecret, setWebhookSecret] = useState('')
  const [showFields, setShowFields] = useState(false)

  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const accountItems = (accountsData?.accounts ?? [])
    .filter(a => a.account_type === 'asset')
    .map(a => ({ id: a.id, label: `${a.code} — ${a.name}` })) as AutocompleteItem[]
  const selectedAccountLabel = gateway.deposit_account_display ?? ''

  const handleSave = () => {
    const data: Record<string, unknown> = {
      gateway_type: gateway.gateway_type,
      is_sandbox: gateway.is_sandbox,
    }
    if (Object.keys(credentials).length > 0) data.credentials = credentials
    if (webhookSecret) data.webhook_secret = webhookSecret
    updateGateway(data as Parameters<typeof updateGateway>[0])
  }

  const handleToggleActive = () => {
    updateGateway({ gateway_type: gateway.gateway_type, is_active: !gateway.is_active })
  }

  const handleToggleSandbox = () => {
    updateGateway({ gateway_type: gateway.gateway_type, is_sandbox: !gateway.is_sandbox })
  }

  if (!info) return null

  return (
    <div className={`rounded-xl border p-5 ${info.color}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-content">{info.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              gateway.is_active ? 'bg-success-surface-strong text-success-content' : 'bg-surface-muted text-content-muted'
            }`}>
              {gateway.is_active ? 'Activa' : 'Inactiva'}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              gateway.is_sandbox ? 'bg-warning-surface-strong text-warning-content' : 'bg-primary-100 text-primary-hover'
            }`}>
              {gateway.is_sandbox ? 'Sandbox' : 'Producción'}
            </span>
          </div>
          <p className="text-xs text-content-muted mt-0.5">{info.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleSandbox}
            disabled={gateway.sandbox_toggle_locked}
            title={gateway.sandbox_toggle_locked ? gateway.sandbox_lock_reason : undefined}
            className={`text-xs px-2 py-1 rounded border border-divider transition-colors ${
              gateway.sandbox_toggle_locked
                ? 'bg-surface-muted text-content-muted cursor-not-allowed opacity-60'
                : 'bg-white/70 text-content-muted hover:bg-surface'
            }`}
          >
            {gateway.sandbox_toggle_locked
              ? 'Sandbox (bloqueado en este ambiente)'
              : (gateway.is_sandbox ? '→ Producción' : '→ Sandbox')}
          </button>
          <button
            onClick={handleToggleActive}
            disabled={updating}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
              gateway.is_active
                ? 'bg-danger-surface-strong text-danger-content hover:bg-danger-border'
                : 'bg-success-fill text-white hover:bg-success-fill-hover'
            }`}
          >
            {gateway.is_active ? 'Desactivar' : 'Activar'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className={`flex items-center gap-1.5 text-xs ${gateway.has_credentials ? 'text-success-content-soft' : 'text-content-subtle'}`}>
          <span className={`w-2 h-2 rounded-full ${gateway.has_credentials ? 'bg-success-solid' : 'bg-content-subtle'}`} />
          {gateway.has_credentials ? 'Credenciales configuradas' : 'Sin credenciales'}
        </div>
        <button
          onClick={() => setShowFields(p => !p)}
          className="text-xs text-primary hover:underline"
        >
          {showFields ? 'Ocultar' : 'Editar credenciales'}
        </button>
      </div>

      {/* Cuenta de depósito — banco/caja donde la pasarela acredita los abonos */}
      <div className="mb-3 pt-3 border-t border-white/50">
        <label htmlFor="configpage-cuenta-deposito" className="block text-xs font-medium text-content-muted mb-1">
          Cuenta de depósito
          <span className="ml-1 text-content-subtle font-normal">— banco/caja donde la pasarela acredita los abonos</span>
        </label>
        <div className="max-w-md">
          <Autocomplete id="configpage-cuenta-deposito"
            items={accountItems}
            value={gateway.deposit_account_id ?? ''}
            displayValue={selectedAccountLabel}
            onChange={() => { /* selección vía onSelect */ }}
            onSelect={(item) => updateGateway({ gateway_type: gateway.gateway_type, deposit_account_id: item.id })}
            placeholder="Buscar cuenta de activo…"
          />
          {gateway.deposit_account_id && (
            <button
              type="button"
              onClick={() => updateGateway({ gateway_type: gateway.gateway_type, deposit_account_id: null })}
              className="mt-1 text-xs text-content-muted hover:text-content underline"
            >
              Quitar cuenta
            </button>
          )}
        </div>
      </div>

      {showFields && (
        <div className="space-y-3 mt-3 pt-3 border-t border-white/50">
          {info.fields.map(field => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-content-muted mb-1">{field.label}</label>
              <input aria-label={field.label}
                type={field.type || 'text'}
                value={field.key === 'webhook_secret' ? webhookSecret : (credentials[field.key] || '')}
                onChange={e => {
                  if (field.key === 'webhook_secret') {
                    setWebhookSecret(e.target.value)
                  } else {
                    setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))
                  }
                }}
                placeholder={field.type === 'password' ? '••••••••' : ''}
                className="w-full px-3 py-2 text-sm border border-white/80 rounded-lg bg-white/70 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={updating}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover transition-colors disabled:opacity-50"
            >
              <Save size={13} /> Guardar
            </button>
            <button
              onClick={() => testGateway(gateway.gateway_type)}
              disabled={testing || !gateway.has_credentials}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-divider text-content rounded-lg text-sm hover:bg-soft transition-colors disabled:opacity-50"
            >
              {testing ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
              Probar conexión
            </button>
            {testResult && (
              <span className={`text-xs font-medium ${(testResult as { success: boolean; message?: string }).success ? 'text-success-content-soft' : 'text-danger-content-soft'}`}>
                {(testResult as { success: boolean; message?: string }).success ? '✓ Conectado' : `✗ ${(testResult as { success: boolean; message?: string }).message}`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Facturador Electrónico Tab ─────────────────────────────────────────────

function InvoicerTab() {
  const navigate = useNavigate()
  const { data: config, isLoading } = useInvoicerConfig()
  const { data: providers } = useInvoicerProviders()
  const { mutate: updateConfig, isPending: isSaving } = useUpdateInvoicerConfig()
  const { mutate: testConnection, isPending: isTesting } = useTestInvoicer()
  const { mutate: updateLocConfig, isPending: isUpdatingLoc } = useUpdateLocalizationConfig()
  const locConfig = useLocalizationConfig()

  const [providerType, setProviderType] = useState('facturadesktop')
  const [isActive, setIsActive] = useState(false)
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [urlsConfig, setUrlsConfig] = useState<Record<string, Record<string, string>>>({})
  const [notes, setNotes] = useState('')
  const [showUrls, setShowUrls] = useState(false)
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({})
  const [initialized, setInitialized] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [environment, setEnvironment] = useState(locConfig.data?.sii_environment || 'homologacion')

  useEffect(() => {
    if (config && !initialized) {
      setProviderType(config.provider_type)
      setIsActive(config.is_active)
      setUrlsConfig(config.urls_config || {})
      setNotes(config.notes || '')
      setInitialized(true)
    }
  }, [config, initialized])

  useEffect(() => {
    if (locConfig.data?.sii_environment) {
      setEnvironment(locConfig.data.sii_environment)
    }
  }, [locConfig.data?.sii_environment])

  const { confirm: confirmarAmbiente, modalProps: modalAmbiente } = useConfirmModal()

  const aplicarAmbiente = (validEnv: 'homologacion' | 'produccion') => {
    setEnvironment(validEnv)
    updateLocConfig(
      { sii_environment: validEnv },
      {
        onSuccess: () => {
          setToast({ type: 'success', message: 'Ambiente actualizado.' })
          setTimeout(() => setToast(null), 3000)
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { error?: { message?: string } } } }
          const message = err.response?.data?.error?.message || 'Error al cambiar ambiente'
          setToast({ type: 'error', message })
          setEnvironment(locConfig.data?.sii_environment || 'homologacion')
          setTimeout(() => setToast(null), 5000)
        },
      }
    )
  }

  const handleEnvironmentChange = (newEnv: string) => {
    const validEnv = (newEnv === 'produccion' ? 'produccion' : 'homologacion') as 'homologacion' | 'produccion'
    if (validEnv === environment) return
    if (validEnv === 'produccion') {
      // El cambio se aplica al instante (este control no pasa por «Guardar»),
      // y en Producción los documentos van al SII REAL con validez tributaria:
      // sin esta confirmación, un clic equivocado deja al tenant emitiendo de
      // verdad sin que nada lo avise.
      confirmarAmbiente({
        message:
          'Al pasar a Producción, los documentos se envían al SII REAL y tienen ' +
          'validez tributaria. El cambio se aplica de inmediato. ¿Cambiar el ambiente a Producción?',
        variant: 'warning',
        confirmLabel: 'Pasar a Producción',
        onConfirm: () => aplicarAmbiente(validEnv),
      })
      return
    }
    aplicarAmbiente(validEnv)
  }

  const currentProvider = providers?.find(p => p.code === providerType)
  const credentialFields = currentProvider?.credential_fields || config?.credential_fields || []
  const urlFields = currentProvider?.url_fields || config?.url_fields || []
  const effectiveUrls = config?.effective_urls || {}

  const handleProviderChange = (newProvider: string) => {
    setProviderType(newProvider)
    setCredentials({})
    setUrlsConfig({})
    setInitialized(true)
  }

  const handleSave = () => {
    const payload: Record<string, unknown> = {
      provider_type: providerType,
      is_active: isActive,
      urls_config: urlsConfig,
      notes,
    }
    const hasNewCreds = Object.values(credentials).some(v => v)
    if (hasNewCreds) {
      payload.credentials = credentials
    }
    updateConfig(payload as Parameters<typeof updateConfig>[0], {
      onSuccess: () => {
        setToast({ type: 'success', message: 'Configuración del facturador guardada.' })
        setCredentials({})
        setTimeout(() => setToast(null), 3000)
      },
      onError: () => {
        setToast({ type: 'error', message: 'Error al guardar la configuración.' })
        setTimeout(() => setToast(null), 5000)
      },
    })
  }

  const handleTest = () => {
    testConnection(undefined, {
      onSuccess: (result) => {
        setToast({
          type: result.success ? 'success' : 'error',
          message: result.message,
        })
        setTimeout(() => setToast(null), 5000)
      },
      onError: () => {
        setToast({ type: 'error', message: 'Error al probar la conexión.' })
        setTimeout(() => setToast(null), 5000)
      },
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-surface-muted rounded-xl animate-pulse" />)}
      </div>
    )
  }

  return (
    <div data-tour="invoicer-page" className="space-y-6 max-w-2xl">
      <ConfirmModal {...modalAmbiente} />
      {toast && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2
          ${toast.type === 'success' ? 'bg-success-surface border border-success-border text-success-content' : 'bg-danger-surface border border-danger-border text-danger-content'}`}>
          {toast.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          {toast.message}
        </div>
      )}

      <p className="text-sm text-content-muted">
        Configura el proveedor de facturación electrónica que se usará para enviar DTEs al organismo fiscal.
      </p>

      {/* Proveedor + Estado */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-content">Proveedor</h3>
              <p className="text-xs text-content-muted mt-0.5">Selecciona el proveedor de facturación a utilizar</p>
            </div>
          </div>

          {/* Ambiente + Estado del Facturador */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {/* Ambiente SII */}
            <div data-tour="invoicer-environment" className="p-4 rounded-lg bg-glass-3 border border-divider">
              <p className="text-xs text-content-muted font-medium mb-2.5">Ambiente SII</p>
              <span className={`inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full ${
                environment === 'produccion'
                  ? 'bg-success-surface-strong text-success-content'
                  : 'bg-warning-surface-strong text-warning-content'
              }`}>
                {environment === 'produccion' ? '🟢 Producción' : '🟡 Homologación'}
              </span>
              <p className="text-xs text-content-subtle mt-2.5">
                {environment === 'produccion'
                  ? 'DTEs se enviarán al SII real'
                  : 'DTEs se enviarán en modo prueba'}
              </p>
              <select aria-label="Ambiente SII"
                value={environment}
                onChange={(e) => handleEnvironmentChange(e.target.value)}
                disabled={isUpdatingLoc}
                className="w-full mt-3 px-2.5 py-1.5 text-xs rounded border border-divider-strong bg-surface text-content focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-50"
              >
                <option value="homologacion">🟡 Homologación (Pruebas)</option>
                <option value="produccion">🟢 Producción</option>
              </select>
            </div>

            {/* Estado del Facturador */}
            <div data-tour="invoicer-active" className="p-4 rounded-lg bg-glass-3 border border-divider">
              <p className="text-xs text-content-muted font-medium mb-2.5">Estado del facturador</p>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-content-subtle peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface after:border-divider-strong after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
              </label>
              <span className="text-xs font-medium text-content ml-2">{isActive ? '✓ Activo' : '✕ Inactivo'}</span>
              <p className="text-xs text-content-subtle mt-2.5">
                {isActive ? 'El facturador está habilitado' : 'El facturador está deshabilitado'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          {(providers ?? []).map(p => (
            <button
              key={p.code}
              onClick={() => handleProviderChange(p.code)}
              className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all
                ${providerType === p.code
                  ? 'border-primary bg-primary-50 text-primary-hover'
                  : 'border-divider text-content-muted hover:border-divider-strong'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Credenciales */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-4">Credenciales</h3>
        {config?.has_credentials && !Object.values(credentials).some(v => v) && (
          <p className="text-xs text-success-content-soft mb-3 flex items-center gap-1">
            <CheckCircle2 size={12} /> Credenciales configuradas
          </p>
        )}
        <div className="space-y-3">
          {credentialFields.map(field => (
            <div key={field.key}>
              <label htmlFor={`configpage-cred-${field.key}`} className="block text-sm font-medium text-content mb-1">
                {field.label}{field.required && <span className="text-danger-solid ml-0.5">*</span>}
              </label>
              <div className="relative">
                <input id={`configpage-cred-${field.key}`}
                  type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                  value={credentials[field.key] || ''}
                  onChange={(e) => setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={config?.has_credentials ? '••••••••' : ''}
                  className={inputCls}
                />
                {field.type === 'password' && (
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted"
                  >
                    {showPasswords[field.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* URLs personalizadas (colapsable) */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <button
          onClick={() => setShowUrls(!showUrls)}
          className="flex items-center justify-between w-full text-sm font-semibold text-content"
        >
          <span>URLs personalizadas</span>
          {showUrls ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {!showUrls && (
          <p className="text-xs text-content-subtle mt-2">
            Se usan las URLs por defecto del proveedor. Expandir para personalizar.
          </p>
        )}

        {showUrls && (
          <div className="mt-4 space-y-4">
            {['homologacion', 'produccion'].map(env => (
              <div key={env}>
                <h4 className="text-xs font-semibold text-content-muted uppercase tracking-wider mb-2">
                  {env === 'produccion' ? 'Producción' : 'Homologación'}
                  {env === environment && (
                    <span className="ml-2 text-primary normal-case tracking-normal">(activo)</span>
                  )}
                </h4>
                <div className="space-y-2">
                  {urlFields.map(field => {
                    const defaultUrl = effectiveUrls[env]?.[field.key] || ''
                    const customUrl = urlsConfig[env]?.[field.key] || ''
                    return (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-content-muted mb-1">{field.label}</label>
                        <input aria-label={field.label}
                          type="text"
                          value={customUrl}
                          onChange={(e) => {
                            setUrlsConfig(prev => ({
                              ...prev,
                              [env]: { ...(prev[env] || {}), [field.key]: e.target.value },
                            }))
                          }}
                          placeholder={defaultUrl}
                          className={`${inputCls} text-xs font-mono`}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notas */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-2">Notas</h3>
        <textarea aria-label="Notas"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Notas internas sobre esta configuración..."
          className={`${inputCls} resize-none`}
        />
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-3">
        <button
          onClick={handleTest}
          disabled={isTesting || !config?.id}
          className="flex items-center gap-2 px-4 py-2.5 border border-divider text-content
            text-sm font-medium rounded-lg hover:bg-soft disabled:opacity-50 transition-colors"
        >
          {isTesting ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          Probar Conexión
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-hover disabled:bg-primary/60
            text-on-primary text-sm font-medium rounded-lg transition-colors"
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Guardar
        </button>
      </div>
    </div>
  )
}

// ─── Payment Gateways Tab ─────────────────────────────────────────────────────

function PaymentGatewaysTab() {
  const { data: gateways, isLoading } = usePaymentGateways()

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-surface-muted rounded-xl animate-pulse" />)}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-content-muted">
        Configura las pasarelas de pago para generar links de pago en facturas y cobrar suscripciones automáticamente.
      </p>
      {(gateways ?? []).map(gw => (
        <GatewayCard key={gw.id} gateway={gw} />
      ))}
    </div>
  )
}

// ─── SSO Config Tab ───────────────────────────────────────────────────────────

function SSOConfigTab() {
  const { data: config, isLoading } = useTenantSSOConfig()
  const { mutate: updateSSO, isPending: isSaving } = useUpdateSSOConfig()
  const { mutate: regenerateSPCert, isPending: isRegenerating } = useRegenerateSAMLSPCert()
  const metadataUrl = useSAMLMetadataUrl()
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const [authMode, setAuthMode] = useState<string>('both')
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [samlEnabled, setSamlEnabled] = useState(false)
  const [samlEntityId, setSamlEntityId] = useState('')
  const [samlSsoUrl, setSamlSsoUrl] = useState('')
  const [samlCertificate, setSamlCertificate] = useState('')
  const [samlAllowIdpInitiated, setSamlAllowIdpInitiated] = useState(false)
  const [samlNameIdFormat, setSamlNameIdFormat] = useState('urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress')
  const [samlAttrEmail, setSamlAttrEmail] = useState('')
  const [samlAttrFirstName, setSamlAttrFirstName] = useState('')
  const [samlAttrLastName, setSamlAttrLastName] = useState('')
  const [showAttrMapping, setShowAttrMapping] = useState(false)
  const [showSetupGuide, setShowSetupGuide] = useState(false)
  const [setupGuideTab, setSetupGuideTab] = useState<'azure' | 'okta' | 'google'>('azure')
  const [showSpCert, setShowSpCert] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Sync local state with fetched config on first load
  useEffect(() => {
    if (config && !initialized) {
      setAuthMode(config.auth_mode)
      setGoogleEnabled(config.google_enabled)
      setSamlEnabled(config.saml_enabled ?? false)
      setSamlEntityId(config.saml_entity_id ?? '')
      setSamlSsoUrl(config.saml_sso_url ?? '')
      setSamlAllowIdpInitiated(config.saml_allow_idp_initiated ?? false)
      setSamlNameIdFormat(config.saml_name_id_format ?? 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress')
      const attrMap = config.saml_attribute_mapping ?? {}
      setSamlAttrEmail(attrMap['email'] ?? '')
      setSamlAttrFirstName(attrMap['first_name'] ?? '')
      setSamlAttrLastName(attrMap['last_name'] ?? '')
      setInitialized(true)
    }
  }, [config, initialized])

  const handleSave = () => {
    const payload: UpdateSSOConfigPayload = {
      auth_mode: authMode as TenantSSOConfig['auth_mode'],
      google_enabled: googleEnabled,
      saml_enabled: samlEnabled,
      saml_entity_id: samlEntityId,
      saml_sso_url: samlSsoUrl,
      saml_allow_idp_initiated: samlAllowIdpInitiated,
      saml_name_id_format: samlNameIdFormat,
      saml_attribute_mapping: {
        ...(samlAttrEmail && { email: samlAttrEmail }),
        ...(samlAttrFirstName && { first_name: samlAttrFirstName }),
        ...(samlAttrLastName && { last_name: samlAttrLastName }),
      },
    }
    if (samlCertificate.trim()) {
      payload.saml_certificate = samlCertificate.trim()
    }
    updateSSO(payload, {
      onSuccess: () => {
        setToast({ type: 'success', message: 'Configuración SSO guardada.' })
        setSamlCertificate('')
        setTimeout(() => setToast(null), 3000)
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { auth_mode?: string[]; saml_enabled?: string[]; saml_certificate?: string[] } } }
        const msg = e?.response?.data?.auth_mode?.[0]
          || e?.response?.data?.saml_enabled?.[0]
          || e?.response?.data?.saml_certificate?.[0]
          || 'Error al guardar configuración SSO.'
        setToast({ type: 'error', message: msg })
        setTimeout(() => setToast(null), 5000)
      },
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-surface-muted rounded-xl animate-pulse" />
        <div className="h-32 bg-surface-muted rounded-xl animate-pulse" />
      </div>
    )
  }

  const AUTH_MODE_OPTIONS = [
    { value: 'both', label: 'Contraseña y SSO', desc: 'Los usuarios pueden elegir entre contraseña o SSO.' },
    { value: 'password_only', label: 'Solo contraseña', desc: 'Deshabilita SSO. Solo login con email y contraseña.' },
    { value: 'sso_only', label: 'Solo SSO', desc: 'Deshabilita login por contraseña. Requiere al menos un proveedor SSO habilitado.' },
  ]

  return (
    <div className="space-y-6 max-w-2xl">
      {toast && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2
          ${toast.type === 'success' ? 'bg-success-surface border border-success-border text-success-content' : 'bg-danger-surface border border-danger-border text-danger-content'}`}>
          {toast.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          {toast.message}
        </div>
      )}

      {/* Modo de autenticación */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-3">Modo de autenticación</h3>
        <div className="space-y-2">
          {AUTH_MODE_OPTIONS.map(opt => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${authMode === opt.value ? 'border-primary bg-primary-50/50' : 'border-divider hover:bg-soft'}`}
            >
              <input
                type="radio"
                name="auth_mode"
                value={opt.value}
                checked={authMode === opt.value}
                onChange={() => setAuthMode(opt.value)}
                className="mt-0.5 text-primary focus:ring-primary"
              />
              <div>
                <p className="text-sm font-medium text-content">{opt.label}</p>
                <p className="text-xs text-content-muted mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Google SSO */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
              <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58Z" fill="#EA4335"/>
            </svg>
            <h3 className="text-sm font-semibold text-content">Google OAuth2</h3>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={googleEnabled}
              onChange={(e) => setGoogleEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-surface-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface after:border-divider-strong after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
          </label>
        </div>
        <p className="text-xs text-content-muted">
          {googleEnabled
            ? 'Los usuarios verán el botón "Iniciar sesión con Google" en la página de login.'
            : 'Habilitar para permitir que los usuarios inicien sesión con su cuenta de Google.'}
        </p>
        {googleEnabled && (
          <p className="text-xs text-content-subtle mt-2">
            Las credenciales de Google OAuth se configuran a nivel de plataforma (variables de entorno del servidor).
          </p>
        )}
      </div>

      {/* SAML 2.0 */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-primary" />
            <h3 className="text-sm font-semibold text-content">SAML 2.0</h3>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={samlEnabled}
              onChange={(e) => setSamlEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-surface-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface after:border-divider-strong after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
          </label>
        </div>
        <p className="text-xs text-content-muted mb-3">
          {samlEnabled
            ? 'SAML habilitado. Los usuarios verán el botón "Iniciar sesión con SSO" en el login.'
            : 'Conecta con Azure AD, Okta, Google Workspace u otro proveedor SAML 2.0.'}
        </p>

        {samlEnabled && (
          <div className="space-y-5 border-t border-divider pt-4">
            {/* IdP Configuration */}
            <div>
              <h4 className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-3">Configuración del proveedor (IdP)</h4>
              <div className="space-y-3">
                <div>
                  <label htmlFor="configpage-entity-id-del-idp" className="block text-xs font-medium text-content mb-1">
                    Entity ID del IdP <span className="text-danger-solid">*</span>
                  </label>
                  <input id="configpage-entity-id-del-idp"
                    value={samlEntityId}
                    onChange={e => setSamlEntityId(e.target.value)}
                    placeholder="https://sts.windows.net/your-tenant-id/"
                    className="w-full px-3 py-2 border border-divider-strong rounded-lg text-sm text-content focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="configpage-sso-url-single-sign-on" className="block text-xs font-medium text-content mb-1">
                    SSO URL (Single Sign-On) <span className="text-danger-solid">*</span>
                  </label>
                  <input id="configpage-sso-url-single-sign-on"
                    value={samlSsoUrl}
                    onChange={e => setSamlSsoUrl(e.target.value)}
                    placeholder="https://login.microsoftonline.com/your-tenant/saml2"
                    className="w-full px-3 py-2 border border-divider-strong rounded-lg text-sm text-content focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="configpage-saml-certificado" className="block text-xs font-medium text-content mb-1">
                    Certificado del IdP (PEM)
                    {config?.has_saml_idp_certificate && (
                      <span className="ml-1 text-success-content-soft font-normal">(cargado)</span>
                    )}
                  </label>
                  <textarea id="configpage-saml-certificado"
                    value={samlCertificate}
                    onChange={e => setSamlCertificate(e.target.value)}
                    rows={4}
                    placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                    className="w-full px-3 py-2 border border-divider-strong rounded-lg text-xs text-content font-mono focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-y"
                  />
                  {config?.has_saml_idp_certificate && !samlCertificate && (
                    <p className="text-xs text-content-subtle mt-1">Deja en blanco para conservar el certificado actual.</p>
                  )}
                  {config?.saml_idp_cert_not_after && (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs text-content-muted">Expiración IdP:</span>
                      <CertExpiryBadge notAfter={config.saml_idp_cert_not_after} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Advanced settings */}
            <div className="border border-divider rounded-lg">
              <button
                type="button"
                onClick={() => setShowAttrMapping(!showAttrMapping)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-content-muted hover:bg-soft rounded-lg"
              >
                <span>Configuración avanzada</span>
                {showAttrMapping ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showAttrMapping && (
                <div className="px-4 pb-4 space-y-4 border-t border-divider pt-3">
                  <div>
                    <label htmlFor="configpage-formato-name-id" className="block text-xs font-medium text-content mb-1">Formato Name ID</label>
                    <select id="configpage-formato-name-id"
                      value={samlNameIdFormat}
                      onChange={e => setSamlNameIdFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-divider-strong rounded-lg text-xs text-content focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-surface"
                    >
                      <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">Email Address</option>
                      <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">Persistent</option>
                      <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">Transient</option>
                      <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">Unspecified</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-content">Permitir inicio desde el portal del IdP</p>
                      <p className="text-xs text-content-muted mt-0.5">Permite acceder sin pasar por la página de login de Clobi.</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
                      <input type="checkbox" checked={samlAllowIdpInitiated} onChange={e => setSamlAllowIdpInitiated(e.target.checked)} className="sr-only peer" />
                      <div className="w-9 h-5 bg-surface-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface after:border-divider-strong after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
                    </label>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-content mb-2">Mapeo de atributos SAML</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label htmlFor="configpage-atributo-email" className="block text-xs text-content-muted mb-1">Atributo email</label>
                        <input id="configpage-atributo-email" value={samlAttrEmail} onChange={e => setSamlAttrEmail(e.target.value)} placeholder="email" className="w-full px-2 py-1.5 border border-divider-strong rounded text-xs focus:ring-1 focus:ring-primary outline-none" />
                      </div>
                      <div>
                        <label htmlFor="configpage-atributo-nombre" className="block text-xs text-content-muted mb-1">Atributo nombre</label>
                        <input id="configpage-atributo-nombre" value={samlAttrFirstName} onChange={e => setSamlAttrFirstName(e.target.value)} placeholder="first_name" className="w-full px-2 py-1.5 border border-divider-strong rounded text-xs focus:ring-1 focus:ring-primary outline-none" />
                      </div>
                      <div>
                        <label htmlFor="configpage-atributo-apellido" className="block text-xs text-content-muted mb-1">Atributo apellido</label>
                        <input id="configpage-atributo-apellido" value={samlAttrLastName} onChange={e => setSamlAttrLastName(e.target.value)} placeholder="last_name" className="w-full px-2 py-1.5 border border-divider-strong rounded text-xs focus:ring-1 focus:ring-primary outline-none" />
                      </div>
                    </div>
                    <p className="text-xs text-content-subtle mt-1">Deja en blanco para usar los valores por defecto del proveedor.</p>
                  </div>
                </div>
              )}
            </div>

            {/* SP Metadata */}
            <div className="bg-glass-3 border border-divider rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-content-muted uppercase tracking-wide">Metadatos del SP (Clobi)</h4>
                <div className="flex items-center gap-2">
                  <a
                    href={metadataUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <Download size={12} /> XML
                  </a>
                  <button
                    type="button"
                    onClick={() => regenerateSPCert(undefined, {
                      onSuccess: () => setToast({ type: 'success', message: 'Certificado SP regenerado. Actualiza el IdP con el nuevo certificado.' })
                    })}
                    disabled={isRegenerating}
                    className="flex items-center gap-1 text-xs text-content-muted hover:text-content border border-divider-strong bg-surface rounded px-2 py-1 hover:bg-soft transition-colors disabled:opacity-50"
                  >
                    {isRegenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Regenerar cert SP
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <CopyableField label="Entity ID / Audience URI" value={`${window.location.origin}/api/v1/auth/saml/metadata/`} />
                <CopyableField label="ACS URL (Assertion Consumer Service)" value={`${window.location.origin}/api/v1/auth/saml/acs/`} />
                <CopyableField label="Metadata URL" value={metadataUrl} />
                {config?.saml_sp_certificate && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-content-muted">Certificado SP (público)</p>
                      <div className="flex items-center gap-2">
                        {config.saml_sp_cert_not_after && <CertExpiryBadge notAfter={config.saml_sp_cert_not_after} />}
                        <button type="button" onClick={() => setShowSpCert(!showSpCert)} className="text-content-subtle hover:text-content-muted">
                          {showSpCert ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                    {showSpCert && (
                      <pre className="text-xs bg-surface border border-divider rounded p-2 text-content overflow-x-auto font-mono whitespace-pre-wrap break-all">
                        {config.saml_sp_certificate}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Setup guide */}
            <div className="border border-divider rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowSetupGuide(!showSetupGuide)}
                className="w-full flex items-center justify-between px-4 py-3 bg-surface-muted text-xs font-medium text-content-muted hover:bg-soft"
              >
                <span>Guía de configuración por proveedor</span>
                {showSetupGuide ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showSetupGuide && (
                <div className="p-4">
                  <div className="flex gap-2 mb-4">
                    {(['azure', 'okta', 'google'] as const).map(tab => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setSetupGuideTab(tab)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                          ${setupGuideTab === tab ? 'bg-primary text-on-primary' : 'bg-surface-muted text-content-muted hover:bg-surface-muted'}`}
                      >
                        {tab === 'azure' ? 'Azure AD' : tab === 'okta' ? 'Okta' : 'Google Workspace'}
                      </button>
                    ))}
                  </div>
                  {setupGuideTab === 'azure' && (
                    <ol className="space-y-2 text-xs text-content-muted list-decimal list-inside">
                      <li>En Azure AD → Enterprise applications → New application → Create your own.</li>
                      <li>En "Set up single sign-on" → SAML.</li>
                      <li>Basic SAML Configuration: pega el Entity ID y el ACS URL de la sección de metadatos.</li>
                      <li>Descarga el "Certificate (Base64)" y pégalo en el campo Certificado del IdP.</li>
                      <li>Copia la "Login URL" al campo SSO URL.</li>
                      <li>Copia el "Azure AD Identifier" al campo Entity ID del IdP.</li>
                    </ol>
                  )}
                  {setupGuideTab === 'okta' && (
                    <ol className="space-y-2 text-xs text-content-muted list-decimal list-inside">
                      <li>En Okta Admin → Applications → Create App Integration → SAML 2.0.</li>
                      <li>Single Sign-On URL: pega el ACS URL de la sección de metadatos.</li>
                      <li>Audience URI (SP Entity ID): pega el Entity ID.</li>
                      <li>En "View SAML setup instructions": copia el Identity Provider SSO URL y el certificado X.509.</li>
                      <li>Pega el SSO URL en el campo SSO URL y el certificado en Certificado del IdP.</li>
                    </ol>
                  )}
                  {setupGuideTab === 'google' && (
                    <ol className="space-y-2 text-xs text-content-muted list-decimal list-inside">
                      <li>En Google Admin → Apps → Web and mobile apps → Add app → Add custom SAML app.</li>
                      <li>Copia el SSO URL y el certificado del IdP que muestra Google.</li>
                      <li>En "Service provider details": pega el ACS URL y el Entity ID.</li>
                      <li>Start URL: déjalo vacío (se usa SP-initiated).</li>
                      <li>Guarda y activa la app para los usuarios correspondientes.</li>
                    </ol>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Guardar */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-hover disabled:bg-primary/60
            text-on-primary text-sm font-medium rounded-lg transition-colors"
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Guardar configuración SSO
        </button>
      </div>
    </div>
  )
}

// ─── API Keys Tab ─────────────────────────────────────────────────────────────

const NOMBRE_DEL_NIVEL = { read: 'Leer', write: 'Escribir', approve: 'Aprobar' } as const

function ScopeSelector({ selected, onChange }: { selected: string[]; onChange: (s: string[]) => void }) {
  const { data: scopeData } = useAPIKeyScopes()
  const groups = scopeData?.groups ?? {}
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  // Solo los niveles que el backend ofrece de verdad: un módulo de solo lectura
  // (la contabilidad) no tiene «Escribir», y mostrar la casilla haría que marcar
  // el grupo completo pidiera un scope inexistente y la key no se pudiera crear.
  const ofrecidos = new Set(scopeData?.scopes ?? [])
  // «Aprobar» solo aparece donde existe (hoy, los comprobantes): una key que
  // registra no contabiliza si no se le da este nivel aparte.
  const nivelesDe = (modulo: string) =>
    (['read', 'write', 'approve'] as const).filter(action => ofrecidos.has(`${modulo}:${action}`))
  const scopesDe = (modulo: string) => nivelesDe(modulo).map(action => `${modulo}:${action}`)

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))
  }

  const toggleScope = (scope: string) => {
    onChange(selected.includes(scope) ? selected.filter(s => s !== scope) : [...selected, scope])
  }

  const toggleParent = (parentKey: string, children: Record<string, string>) => {
    const allScopes = [...scopesDe(parentKey), ...Object.keys(children).flatMap(scopesDe)]
    const allSelected = allScopes.every(s => selected.includes(s))
    if (allSelected) {
      onChange(selected.filter(s => !allScopes.includes(s)))
    } else {
      onChange([...new Set([...selected, ...allScopes])])
    }
  }

  return (
    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
      {Object.entries(groups).map(([groupKey, group]) => {
        const childKeys = Object.keys(group.children)
        const allGroupScopes = [...scopesDe(groupKey), ...childKeys.flatMap(scopesDe)]
        const selectedCount = allGroupScopes.filter(s => selected.includes(s)).length
        const isExpanded = expandedGroups[groupKey] ?? false

        return (
          <div key={groupKey} className="border border-divider rounded-lg overflow-hidden">
            <div
              className="flex items-center justify-between px-3 py-2 bg-surface-muted cursor-pointer hover:bg-soft transition-colors"
              onClick={() => toggleGroup(groupKey)}
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleParent(groupKey, group.children) }}
                  className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                    selectedCount === allGroupScopes.length
                      ? 'bg-primary border-primary text-on-primary'
                      : selectedCount > 0
                        ? 'bg-primary-100 border-primary-200 text-primary'
                        : 'border-divider-strong'
                  }`}
                >
                  {selectedCount === allGroupScopes.length ? '✓' : selectedCount > 0 ? '−' : ''}
                </button>
                <span className="text-xs font-semibold text-content">{group.label}</span>
                {selectedCount > 0 && (
                  <span className="text-[10px] bg-primary-50 text-primary-hover px-1.5 rounded-full">{selectedCount}</span>
                )}
              </div>
              <ChevronRight size={14} className={`text-content-subtle transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </div>

            {isExpanded && (
              <div className="px-3 py-2 space-y-1.5">
                {/* Parent scope */}
                <div className="flex items-center gap-3 pb-1.5 border-b border-divider">
                  <span className="text-[10px] text-content-subtle flex-1 min-w-0">
                    Módulo completo
                    {/* Un módulo sin hijos (Cuentas por pagar) no tiene fila propia
                        abajo: su descripción va acá o no se ve en ningún lado. */}
                    {childKeys.length === 0 && group.descriptions?.[groupKey] && (
                      <span className="block text-[11px] text-content-subtle mt-0.5">
                        {group.descriptions[groupKey]}
                      </span>
                    )}
                  </span>
                  {nivelesDe(groupKey).map(action => (
                    <label key={action} className="flex items-center gap-1.5 text-xs text-content-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.includes(`${groupKey}:${action}`)}
                        onChange={() => toggleScope(`${groupKey}:${action}`)}
                        className="rounded border-divider-strong text-primary focus:ring-primary w-3.5 h-3.5"
                      />
                      {NOMBRE_DEL_NIVEL[action]}
                    </label>
                  ))}
                </div>
                {/* Children */}
                {childKeys.map(childKey => (
                  <div key={childKey} className="flex items-start gap-3">
                    <span className="text-xs text-content-muted flex-1 min-w-0">
                      {group.children[childKey]}
                      {/* Qué habilita el scope, en palabras del integrador. Sin esto
                          el cliente marca casillas sin saber qué está otorgando. */}
                      {group.descriptions?.[childKey] && (
                        <span className="block text-[11px] text-content-subtle mt-0.5">
                          {group.descriptions[childKey]}
                        </span>
                      )}
                    </span>
                    {nivelesDe(childKey).map(action => (
                      <label key={action} className="flex items-center gap-1.5 text-xs text-content-muted cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected.includes(`${childKey}:${action}`)}
                          onChange={() => toggleScope(`${childKey}:${action}`)}
                          className="rounded border-divider-strong text-primary focus:ring-primary w-3.5 h-3.5"
                        />
                        {NOMBRE_DEL_NIVEL[action]}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function APIKeyUsagePanel({ keyId, onClose }: { keyId: string; onClose: () => void }) {
  const [days, setDays] = useState(7)
  const { data, isLoading } = useAPIKeyUsage(keyId, days)
  const locale = useTenantLocale()

  if (isLoading) return (
    <div className="flex items-center justify-center py-10">
      <Loader2 className="animate-spin text-content-subtle" size={20} />
    </div>
  )

  if (!data) return null

  const maxReqs = Math.max(...(data.daily.map(d => d.requests) || [1]))

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <BarChart3 size={16} className="text-primary shrink-0" />
          Uso de {data.key_name}
        </span>
      }
    >
      <div className="space-y-5">
        <div className="flex justify-end">
          <select
            aria-label="Período"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-xs border border-divider rounded px-2 py-1"
          >
            <option value={7}>7 días</option>
            <option value={14}>14 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
          </select>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: data.summary.total_requests.toLocaleString(), color: 'text-content' },
            { label: 'Exitosos', value: data.summary.successful.toLocaleString(), color: 'text-success-content' },
            { label: 'Errores', value: `${data.summary.errors} (${data.summary.error_rate}%)`, color: data.summary.error_rate > 10 ? 'text-danger-content-soft' : 'text-warning-content-soft' },
            { label: 'Latencia prom.', value: `${data.summary.avg_response_ms}ms`, color: 'text-primary-hover' },
          ].map(card => (
            <div key={card.label} className="bg-glass-3 rounded-lg p-3">
              <p className="text-[10px] text-content-muted uppercase">{card.label}</p>
              <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Daily chart (simple bar) */}
        {data.daily.length > 0 && (
          <div>
            <p className="text-xs font-medium text-content-muted mb-2">Requests por día</p>
            <div className="flex items-end gap-1 h-24">
              {data.daily.map(d => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.requests} req, ${d.errors} err`}>
                  <div className="w-full flex flex-col justify-end" style={{ height: '80px' }}>
                    {d.errors > 0 && (
                      <div
                        className="w-full bg-danger-solid rounded-t"
                        style={{ height: `${Math.max((d.errors / maxReqs) * 80, 2)}px` }}
                      />
                    )}
                    <div
                      className="w-full bg-primary rounded-t"
                      style={{ height: `${Math.max(((d.requests - d.errors) / maxReqs) * 80, 1)}px` }}
                    />
                  </div>
                  <span className="text-[8px] text-content-subtle">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top endpoints */}
        {data.top_endpoints.length > 0 && (
          <div>
            <p className="text-xs font-medium text-content-muted mb-2">Top endpoints</p>
            <div className="space-y-1">
              {data.top_endpoints.slice(0, 5).map((ep, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] font-medium ${
                    ep.method === 'GET' ? 'bg-success-surface text-success-content' :
                    ep.method === 'POST' ? 'bg-primary-50 text-primary-hover' :
                    ep.method === 'DELETE' ? 'bg-danger-surface text-danger-content' : 'bg-warning-surface text-warning-content'
                  }`}>{ep.method}</span>
                  <span className="text-content-muted flex-1 truncate font-mono">{ep.endpoint}</span>
                  <span className="text-content-subtle font-medium">{ep.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent errors */}
        {data.recent_errors.length > 0 && (
          <div>
            <p className="text-xs font-medium text-danger-solid mb-2">Errores recientes</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {data.recent_errors.slice(0, 10).map((err, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-danger-surface rounded px-2 py-1.5">
                  <span className="font-mono text-danger-content-soft font-bold">{err.status_code}</span>
                  <span className="text-danger-content truncate flex-1">{err.method} {err.endpoint}</span>
                  <span className="text-danger-solid text-[10px]">
                    {formatDateTime(err.timestamp, locale)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-content-subtle text-center">
          {data.summary.unique_ips} IP(s) única(s) en el período · Rate limit: 60 req/min por key
        </p>
      </div>
    </Modal>
  )
}

function APIKeysTab() {
  const { data, isLoading } = useAPIKeys()
  const createMutation = useCreateAPIKey()
  const updateMutation = useUpdateAPIKey()
  const deleteMutation = useDeleteAPIKey()
  const { data: alertsData } = useAPIKeyAlerts()
  const { data: globalUsage } = useAPIKeyGlobalUsage()
  const locale = useTenantLocale()

  const [showModal, setShowModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newScopes, setNewScopes] = useState<string[]>([])
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [usageKeyId, setUsageKeyId] = useState<string | null>(null)
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const keys = data?.api_keys ?? []
  const alerts = alertsData?.alerts ?? []

  const handleCreate = () => {
    if (!newName.trim()) return
    createMutation.mutate({
      name: newName,
      scopes: newScopes,
      ...(expiresInDays ? { expires_in_days: Number(expiresInDays) } : {}),
    }, {
      onSuccess: (result) => {
        setCreatedKey(result.key || null)
        setNewName('')
        setNewScopes([])
        setExpiresInDays('')
      },
    })
  }

  const handleCopy = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleToggle = (key: APIKeyItem) => {
    updateMutation.mutate({ id: key.id, data: { is_active: !key.is_active } })
  }

  const handleDelete = (key: APIKeyItem) => {
    confirmAction({
      message: `¿Eliminar API Key "${key.name}"?`,
      variant: 'danger',
      onConfirm: () => {
        deleteMutation.mutate(key.id)
      },
    })
  }

  return (
    <div className="space-y-4">
      <ConfirmModal {...modalProps} />
      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.slice(0, 3).map((alert, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
              alert.severity === 'critical' ? 'bg-danger-surface text-danger-content border border-danger-border' :
              alert.severity === 'warning' ? 'bg-warning-surface text-warning-content border border-warning-border' :
              'bg-primary-50 text-primary-hover border border-primary-200'
            }`}>
              {alert.severity === 'critical' ? <AlertCircle size={14} /> :
               alert.severity === 'warning' ? <Bell size={14} /> : <Activity size={14} />}
              <span className="font-medium">{alert.key_name}:</span>
              <span className="flex-1">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Global usage summary */}
      {globalUsage && globalUsage.total_requests > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-glass-3 rounded-lg p-3">
            <p className="text-[10px] text-content-muted uppercase">Total requests (7d)</p>
            <p className="text-lg font-bold text-content">{globalUsage.total_requests.toLocaleString()}</p>
          </div>
          <div className="bg-glass-3 rounded-lg p-3">
            <p className="text-[10px] text-content-muted uppercase">Errores</p>
            <p className={`text-lg font-bold ${globalUsage.error_rate > 10 ? 'text-danger-content-soft' : 'text-success-content'}`}>
              {globalUsage.total_errors} ({globalUsage.error_rate}%)
            </p>
          </div>
          <div className="bg-glass-3 rounded-lg p-3">
            <p className="text-[10px] text-content-muted uppercase">Keys activas</p>
            <p className="text-lg font-bold text-primary-hover">{keys.filter(k => k.is_active).length}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={async () => {
            const res = await apiClient.get('/config/api-keys/export/postman/', { responseType: 'blob' })
            const url = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = url
            a.download = 'Zen_API.postman_collection.json'
            a.click()
            URL.revokeObjectURL(url)
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-divider text-content-muted text-xs font-medium rounded-lg hover:bg-soft transition-colors"
          title="Descargar colección Postman"
        >
          <Download size={13} />
          Postman
        </button>
        <button
          onClick={async () => {
            const res = await apiClient.get('/config/api-keys/export/openapi/', { responseType: 'blob' })
            const url = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = url
            a.download = 'Zen_API.openapi.json'
            a.click()
            URL.revokeObjectURL(url)
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-divider text-content-muted text-xs font-medium rounded-lg hover:bg-soft transition-colors"
          title="Descargar OpenAPI spec (importable en Postman, Insomnia, etc.)"
        >
          <Download size={13} />
          OpenAPI
        </button>
        <button
          onClick={() => { setShowModal(true); setCreatedKey(null) }}
          className="px-3 py-1.5 bg-primary text-on-primary text-xs font-medium rounded-lg hover:bg-primary-hover transition-colors"
        >
          Nueva API Key
        </button>
      </div>

      <DataTable
        rows={keys}
        rowKey={k => k.id}
        loading={isLoading}
        loadingNode={
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-content-subtle" size={20} />
          </div>
        }
        minWidth={880}
        caption="API Keys de la empresa"
        empty={<EmptyState title="No hay API Keys creadas" size="sm" />}
        columns={[
          {
            key: 'name', header: 'Nombre', sortable: true,
            render: k => <span className="font-medium text-content">{k.name}</span>,
          },
          {
            key: 'prefix', header: 'Prefijo', width: 160,
            render: k => (
              <span className="font-mono text-xs text-content-muted">zen_live_{k.prefix}…</span>
            ),
          },
          {
            key: 'scopes', header: 'Scopes',
            render: k => (
              <div className="flex flex-wrap gap-1">
                {k.scopes.length <= 4
                  ? k.scopes.map(s => (
                      <span key={s} className="px-1.5 py-0.5 bg-primary-50 text-primary-hover text-[10px] font-medium rounded">
                        {s}
                      </span>
                    ))
                  : <>
                      {k.scopes.slice(0, 2).map(s => (
                        <span key={s} className="px-1.5 py-0.5 bg-primary-50 text-primary-hover text-[10px] font-medium rounded">
                          {s}
                        </span>
                      ))}
                      <span className="px-1.5 py-0.5 bg-surface-muted text-content-muted text-[10px] font-medium rounded">
                        +{k.scopes.length - 2} más
                      </span>
                    </>
                }
              </div>
            ),
          },
          {
            key: 'last_used_at', header: 'Último uso', sortable: true, width: 140,
            // ⚠️ Se ordena por el ISO crudo y no por lo que imprime `formatDate`:
            // como texto, «02-01-2026» iría antes que «15-09-2025». Una key que
            // nunca se usó va como vacío, así queda al final suba o baje el orden.
            sortValue: k => k.last_used_at ?? null,
            render: k => (
              <span className="text-xs text-content-muted">
                {k.last_used_at ? formatDate(k.last_used_at, locale) : 'Nunca'}
              </span>
            ),
          },
          {
            key: 'is_active', header: 'Estado', sortable: true, width: 110,
            sortValue: k => (k.is_active ? 1 : 0),
            render: k => (
              <div className="text-center">
                <button
                  onClick={() => handleToggle(k)}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                    k.is_active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'
                  }`}
                >
                  {k.is_active ? 'Activo' : 'Inactivo'}
                </button>
              </div>
            ),
          },
          {
            key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 90,
            render: k => (
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => setUsageKeyId(k.id)}
                  className="p-1 text-primary hover:text-primary transition-colors"
                  title="Ver métricas"
                  aria-label="Ver métricas de la API key"
                >
                  <BarChart3 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(k)}
                  className="p-1 text-danger-solid hover:text-danger-content transition-colors"
                  title="Eliminar"
                  aria-label="Eliminar API key"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ),
          },
        ]}
      />

      {/* Usage panel */}
      {usageKeyId && (
        <APIKeyUsagePanel keyId={usageKeyId} onClose={() => setUsageKeyId(null)} />
      )}

      {/* Modal crear API Key */}
      {showModal && (
        <Modal
          open
          onClose={() => setShowModal(false)}
          size="md"
          title={createdKey ? 'API Key creada' : 'Nueva API Key'}
          actions={createdKey ? (
            <button
              onClick={() => { setShowModal(false); setCreatedKey(null) }}
              className="px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors"
            >
              Cerrar
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-divider text-content text-sm font-medium rounded-lg hover:bg-soft transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || newScopes.length === 0 || createMutation.isPending}
                className="px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creando…' : 'Crear'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            {createdKey ? (
              <>
                <p className="text-xs text-content-muted">Copia esta clave ahora. No se mostrará de nuevo.</p>
                <div className="flex items-center gap-2 bg-glass-3 border border-divider rounded-lg p-3">
                  <code className="text-xs font-mono text-content flex-1 break-all">{createdKey}</code>
                  <button onClick={handleCopy} aria-label="Copiar la clave" className="p-1.5 hover:bg-surface-muted rounded transition-colors">
                    {copied ? <Check size={14} className="text-success-content-soft" /> : <Copy size={14} className="text-content-subtle" />}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="configpage-nombre" className="block text-xs font-medium text-content-muted mb-1">Nombre</label>
                  <input id="configpage-nombre"
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Ej: Webhook Shopify"
                    className="w-full px-3 py-2 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="configpage-expiracion-opcional" className="block text-xs font-medium text-content-muted mb-1">Expiración (opcional)</label>
                  <select id="configpage-expiracion-opcional"
                    value={expiresInDays}
                    onChange={e => setExpiresInDays(e.target.value ? Number(e.target.value) : '')}
                    className="w-full px-3 py-2 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Sin expiración</option>
                    <option value={30}>30 días</option>
                    <option value={90}>90 días</option>
                    <option value={180}>180 días</option>
                    <option value={365}>1 año</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-muted mb-2">Permisos (scopes)</label>
                  <ScopeSelector selected={newScopes} onChange={setNewScopes} />
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

// ─── CurrencyRatesTab ────────────────────────────────────────────────────────

function CurrencyRatesTab() {
  const { data, isLoading } = useCurrencyRates()
  const syncMutation = useSyncCurrencyRates()

  const currencyLabels: Record<string, string> = {
    USD: 'Dólar Estadounidense',
    EUR: 'Euro',
    UF: 'Unidad de Fomento',
  }

  const rates = data?.rates ?? {}
  const hasRates = Object.keys(rates).length > 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button
          onClick={() => syncMutation.mutate(undefined)}
          disabled={syncMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60"
        >
          {syncMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Sincronizar
        </button>
      </div>

      {syncMutation.isSuccess && (
        <div className="px-4 py-3 bg-success-surface border border-success-border rounded-lg text-sm text-success-content flex items-center gap-2">
          <CheckCircle2 size={15} />
          Tipos de cambio sincronizados correctamente.
        </div>
      )}

      {syncMutation.isError && (
        <div className="px-4 py-3 bg-danger-surface border border-danger-border rounded-lg text-sm text-danger-content flex items-center gap-2">
          <AlertCircle size={15} />
          Error al sincronizar. Intenta nuevamente.
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-content-subtle" />
        </div>
      ) : !hasRates ? (
        <div className="text-center py-12 text-content-subtle">
          <DollarSign size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No hay tipos de cambio registrados.</p>
          <p className="text-xs mt-1">Presiona "Sincronizar" para obtener las tasas actuales.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {Object.entries(rates).map(([code, info]) => (
            <div key={code} className="bg-glass backdrop-blur-glass border border-divider rounded-card p-4 space-y-2 shadow-card">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-content-muted tracking-wider">{code}</span>
                <span className="text-xs text-content-subtle">{info.rate_date}</span>
              </div>
              <p className="text-2xl font-semibold text-content">
                ${parseFloat(info.rate).toLocaleString('es-CL')}
                <span className="text-sm font-normal text-content-subtle ml-1">CLP</span>
              </p>
              <p className="text-xs text-content-muted">{currencyLabels[code] || info.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Certificados y CAF ──────────────────────────────────────────────────────

function CertificatesTab() {
  const { data: certInfo, isLoading: certLoading } = useCertificateInfo()
  const uploadCert = useUploadCertificate()

  const pfxRef = useRef<HTMLInputElement>(null)
  const [password, setPassword] = useState('')
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 4000)
  }

  const handleUploadPFX = () => {
    const file = pfxRef.current?.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append('certificate', file)
    fd.append('password', password)
    uploadCert.mutate(fd, {
      onSuccess: (data) => {
        showToast(data.message || 'Certificado cargado.', 'success')
        setPassword('')
        if (pfxRef.current) pfxRef.current.value = ''
      },
      onError: (err: any) => showToast(err?.response?.data?.error?.message || 'Error.', 'error'),
    })
  }

  if (certLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
  }

  return (
    <div className="space-y-8">
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] px-4 py-2 rounded-lg text-sm shadow-lg ${
          toast.type === 'success' ? 'bg-success-fill text-white' : 'bg-danger-fill text-white'
        }`}>{toast.msg}</div>
      )}

      {/* Certificado Digital */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-card">
        <h3 className="text-base font-semibold text-content mb-4 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" /> Certificado Digital (PFX)
        </h3>

        {certInfo?.has_certificate ? (
          <div className="space-y-3">
            {/* Expiration card — prominent */}
            {certInfo.valid_until && (() => {
              const days = certInfo.days_remaining ?? null
              const expired = days != null && days < 0
              const critical = days != null && days >= 0 && days < 7
              const warning = days != null && days >= 7 && days < 30
              const caution = days != null && days >= 30 && days < 60
              const cfg = expired
                ? { bg: 'bg-danger-surface', border: 'border-danger-border-strong', text: 'text-danger-content', label: 'VENCIDO' }
                : critical
                ? { bg: 'bg-danger-surface', border: 'border-danger-border-strong', text: 'text-danger-content', label: 'Crítico' }
                : warning
                ? { bg: 'bg-orange-50 dark:bg-orange-900/30', border: 'border-orange-300 dark:border-orange-700', text: 'text-orange-700 dark:text-orange-300', label: 'Próximo a vencer' }
                : caution
                ? { bg: 'bg-warning-surface', border: 'border-warning-border-strong', text: 'text-warning-content', label: 'Revisar pronto' }
                : { bg: 'bg-success-surface', border: 'border-emerald-300 dark:border-emerald-700', text: 'text-success-content', label: 'Vigente' }
              return (
                <div className={`flex items-center justify-between gap-4 p-4 rounded-xl border-2 ${cfg.bg} ${cfg.border}`}>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wider ${cfg.text}`}>Vencimiento — {cfg.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${cfg.text}`}>
                      {certInfo.valid_until.split('T')[0]}
                    </p>
                    {days != null && (
                      <p className={`text-sm mt-0.5 ${cfg.text} opacity-90`}>
                        {expired ? `Venció hace ${Math.abs(days)} día${Math.abs(days) !== 1 ? 's' : ''}` : `${days} día${days !== 1 ? 's' : ''} restante${days !== 1 ? 's' : ''}`}
                      </p>
                    )}
                  </div>
                  <div className={`p-3 rounded-full ${cfg.bg} ${cfg.border} border`}>
                    {certInfo.is_valid
                      ? <CheckCircle2 className={`w-8 h-8 ${cfg.text}`} />
                      : <AlertCircle className={`w-8 h-8 ${cfg.text}`} />}
                  </div>
                </div>
              )
            })()}

            <div className="flex items-start gap-3 p-3 bg-glass-3 rounded-lg">
              <div className="flex-1 text-sm">
                <div className="font-medium text-content">{certInfo.subject}</div>
                <div className="text-content-muted text-xs mt-1">
                  Emisor: {certInfo.issuer}
                </div>
              </div>
            </div>
            {certInfo.error && (
              <div className="text-xs text-danger-content-soft bg-danger-surface p-2 rounded">{certInfo.error}</div>
            )}
          </div>
        ) : (
          <p className="text-sm text-content-muted">No hay certificado configurado.</p>
        )}

        <div className="mt-4 pt-4 border-t space-y-3">
          <p className="text-xs text-content-muted">
            {certInfo?.has_certificate ? 'Reemplazar certificado:' : 'Subir certificado PFX/P12:'}
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="configpage-archivo-pfx" className="block text-xs text-content-muted mb-1">Archivo PFX</label>
              <input id="configpage-archivo-pfx" ref={pfxRef} type="file" accept=".pfx,.p12" className="text-sm" />
            </div>
            <div className="w-48">
              <label htmlFor="configpage-contrasena" className="block text-xs text-content-muted mb-1">Contraseña</label>
              <input id="configpage-contrasena"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={inputCls}
                placeholder="Contraseña del PFX"
              />
            </div>
            <button
              onClick={handleUploadPFX}
              disabled={uploadCert.isPending}
              className="px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover disabled:opacity-50 flex items-center gap-2"
            >
              {uploadCert.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Subir
            </button>
          </div>
        </div>
      </div>

    </div>
  )
}

// ─── ConfigPage ───────────────────────────────────────────────────────────────

const SECTION_TITLES: Record<string, { title: string; subtitle: string; helpKey?: string }> = {
  profile:       { title: 'Perfil de Empresa',    subtitle: 'Datos legales, logo y color del sidebar.', helpKey: 'config.profile' },
  localization:  { title: 'Localización',          subtitle: 'País, moneda, impuestos, régimen tributario y certificado digital.', helpKey: 'config.localization' },
  currency:      { title: 'Tipos de Cambio',       subtitle: 'Tasas de conversión para facturación multi-moneda.', helpKey: 'config.currency' },
  folios:        { title: 'Folios y CAF',             subtitle: 'Folios autorizados y Códigos de Autorización del SII.', helpKey: 'config.folios' },
  fields:        { title: 'Campos Personalizados',  subtitle: 'Campos adicionales para ventas, compras y contratos.', helpKey: 'config.fields' },
  templates:     { title: 'Plantillas PDF',          subtitle: 'Formato de los documentos impresos.', helpKey: 'config.templates' },
  invoicer:      { title: 'Facturador Electrónico', subtitle: 'Proveedor de facturación electrónica.', helpKey: 'config.invoicer' },
  gateways:      { title: 'Pasarelas de Pago',      subtitle: 'Configuración de pasarelas para cobros online.', helpKey: 'config.gateways' },
  sso:           { title: 'SSO',                     subtitle: 'Inicio de sesión: contraseña, Google o SAML 2.0.', helpKey: 'config.sso' },
  'api-keys':    { title: 'API Keys',                subtitle: 'Claves de acceso para integraciones externas.', helpKey: 'config.api-keys' },
  certificates:  { title: 'Certificado Digital',     subtitle: 'Certificado PFX para firma electrónica de DTEs.', helpKey: 'config.certificates' },
  'dte-types':   { title: 'Documentos DTE',          subtitle: 'Tipos de documento y campos del esquema SII habilitados.', helpKey: 'config.dte-types' },
  'notifications': { title: 'Notificaciones',        subtitle: 'Canales default por evento para toda la empresa.', helpKey: 'config.notifications' },
  'audit-log':   { title: 'Bitácora de Actividad',   subtitle: 'Historial de cambios en los datos del ERP — quién hizo qué y cuándo.', helpKey: 'config.audit-log' },
  'hr-documents': { title: 'Documentos de RRHH',       subtitle: 'Tipos de documento y plantillas de carpetas para la carpeta digital del trabajador.', helpKey: 'config.hr-documents' },
  'historical-import': { title: 'Importación Histórica', subtitle: 'Migra DTEs históricos desde XMLs sin tocar la contabilidad ya cargada.', helpKey: 'config.historical-import' },
}

export function ConfigPage() {
  const location = useLocation()
  const segment = location.pathname.split('/config/')[1] || 'profile'
  const [helpOpen, setHelpOpen] = useState(false)

  // La numeración de documentos es la misma sección de Configuración en todos
  // los países, pero no el mismo concepto: CAF con rango autorizado en Chile,
  // serie declarada por el emisor en Perú. Un tenant peruano que entra a
  // «Folios y CAF» no encontraría dónde crear sus series.
  const { data: loc } = useLocalizationConfig()
  const conSeries = usaSeriesDeEmision(loc?.country_code)

  const baseSection = SECTION_TITLES[segment] || SECTION_TITLES.profile
  const section = segment === 'folios'
    ? {
        ...baseSection,
        ...etiquetasNumeracion(loc?.country_code),
        helpKey: conSeries ? 'config.series' : 'config.folios',
      }
    : baseSection
  const activeTab = segment
  const rawHelp = section.helpKey ? HELP[section.helpKey] : undefined
  // Resolve dynamic placeholders in help content
  const helpContent = rawHelp ? {
    ...rawHelp,
    sections: rawHelp.sections.map(s => {
      if (!s.content.includes('{{API_BASE_URL}}')) return s
      const { protocol, hostname } = window.location
      const baseDomain = hostname.includes('.') ? hostname.split('.').slice(-2).join('.') : hostname
      const exampleUrl = `${protocol}//{tu-empresa}.${baseDomain}/api/v1/`
      return { ...s, content: s.content.replace('{{API_BASE_URL}}', exampleUrl) }
    }),
  } : undefined

  // ⚠️ El ancho no puede ser el mismo para todas las pestañas. Casi todas son
  // formularios, donde una columna angosta se lee mejor; la de folios es una
  // tabla de 8 columnas que en `max-w-5xl` quedaba cortada sin que se vieran las
  // últimas. El límite se levanta solo donde el contenido lo pide.
  // Folios lleva una tabla ancha; las plantillas, un editor de tres columnas.
  const anchoDelContenido = activeTab === 'templates' ? 'max-w-[1680px]' : activeTab === 'folios' ? 'max-w-7xl' : 'max-w-5xl'

  return (
    <div className={`p-6 ${anchoDelContenido} mx-auto`}>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-content">{section.title}</h1>
          {helpContent && (
            <button
              onClick={() => setHelpOpen(true)}
              className="p-1 rounded-full text-content-subtle hover:text-primary hover:bg-primary-50 transition-colors"
              title="Más información sobre este módulo"
              aria-label="Más información sobre este módulo"
            >
              <HelpCircle size={16} />
            </button>
          )}
        </div>
        <p className="text-sm text-content-muted mt-0.5">{section.subtitle}</p>
      </div>

      {helpContent && (
        <HelpPanel
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title={helpContent.title}
          sections={helpContent.sections}
        />
      )}

      {activeTab === 'profile' && <CompanyProfileTab />}
      {activeTab === 'localization' && <LocalizationTabWithCert />}
      {activeTab === 'currency' && <CurrencyRatesTab />}
      {activeTab === 'folios' && (
        conSeries ? <SeriesTab countryCode={loc?.country_code} /> : <FoliosTab />
      )}
      {activeTab === 'fields' && <CustomFieldsTab />}
      {activeTab === 'templates' && <PdfTemplatesTab />}
      {activeTab === 'invoicer' && <InvoicerTab />}
      {activeTab === 'gateways' && <PaymentGatewaysTab />}
      {activeTab === 'sso' && <SSOConfigTab />}
      {activeTab === 'api-keys' && <APIKeysTab />}
      {activeTab === 'certificates' && <CertificatesTab />}
      {activeTab === 'dte-types' && <DTEConfigTab />}
      {activeTab === 'notifications' && <TenantNotificationsTab />}
      {activeTab === 'audit-log' && <AuditLogTab />}
      {activeTab === 'hr-documents' && <HrDocumentSettingsTab />}
      {activeTab === 'historical-import' && <HistoricalImportPage />}
    </div>
  )
}
